import React, { useState, useEffect, useRef } from 'react';
import { safeUrl } from '../lib/safeUrl';
import { formatDateInTZ } from '../lib/localDate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { OnboardingEmptyState } from '../onboarding';
import { STANDARD_INDUSTRIES, getIndustryLabel, isValidIndustryId } from '../constants/industries';
import IndustryMultiSelectFilter from '../components/IndustryMultiSelectFilter';
import LinkedEntityFilter from '../components/LinkedEntityFilter';
import { AdminNewsDialog } from '../components/dialogs/AdminNewsDialog';
import { usePlanGate } from '../lib/planGate';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { buildCountryGate } from '../lib/countryGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import ManageLinksDialog from '../components/dialogs/ManageLinksDialog';
import EntityCardActions from '../components/EntityCardActions';
import FavoriteButton from '../components/FavoriteButton';
import useFavorites from '../hooks/useFavorites';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import LinkedItemsPopover from '../components/LinkedItemsPopover';
import { 
  Newspaper, Search, Filter, Globe, Calendar, Building2, 
  ExternalLink, ChevronLeft, ChevronRight, Sparkles, MapPin,
  Clock, TrendingUp, RefreshCw, Settings, Eye, EyeOff, Plus,
  Zap, Users, Briefcase, ArrowRight, Check, X,
  Rss, Star, StarOff, ChevronDown, CalendarDays, Factory, UserPlus, Loader2,
  Fuel, Building, Plane, Landmark, Stethoscope, GraduationCap, Cpu, Hotel, Ship,
  Shield, Truck, ArrowLeft, LayoutDashboard, Link2, Heart, MoreHorizontal,
  Pencil, Trash2, MessageCircle, Linkedin, User, FileText, History,
  Square, AlertTriangle
} from 'lucide-react';
import { trackSearch, trackFeature, trackAI } from '../services/eventTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sector-specific styling for placeholder images
const sectorConfig = {
  'Construction': { 
    icon: Building2, 
    gradient: 'from-amber-600 via-orange-500 to-yellow-500',
    iconColor: 'text-white/90'
  },
  'Oil & Gas': { 
    icon: Fuel, 
    gradient: 'from-slate-800 via-gray-700 to-zinc-600',
    iconColor: 'text-white/90'
  },
  'Real Estate': { 
    icon: Building, 
    gradient: 'from-emerald-600 via-green-500 to-teal-500',
    iconColor: 'text-white/90'
  },
  'Technology': { 
    icon: Cpu, 
    gradient: 'from-blue-600 via-indigo-500 to-violet-500',
    iconColor: 'text-white/90'
  },
  'Healthcare': { 
    icon: Stethoscope, 
    gradient: 'from-red-500 via-rose-500 to-pink-500',
    iconColor: 'text-white/90'
  },
  'Finance': { 
    icon: Landmark, 
    gradient: 'from-green-700 via-emerald-600 to-teal-600',
    iconColor: 'text-white/90'
  },
  'Tourism': { 
    icon: Hotel, 
    gradient: 'from-cyan-600 via-sky-500 to-blue-500',
    iconColor: 'text-white/90'
  },
  'Transportation': { 
    icon: Ship, 
    gradient: 'from-indigo-600 via-purple-500 to-violet-500',
    iconColor: 'text-white/90'
  },
  'Education': { 
    icon: GraduationCap, 
    gradient: 'from-purple-600 via-fuchsia-500 to-pink-500',
    iconColor: 'text-white/90'
  },
  'Manufacturing': { 
    icon: Factory, 
    gradient: 'from-zinc-700 via-gray-600 to-slate-600',
    iconColor: 'text-white/90'
  },
  'Defense': { 
    icon: Shield, 
    gradient: 'from-slate-800 via-gray-700 to-zinc-700',
    iconColor: 'text-white/90'
  },
  'Energy': { 
    icon: Zap, 
    gradient: 'from-yellow-500 via-amber-500 to-orange-500',
    iconColor: 'text-white/90'
  },
  'Aviation': { 
    icon: Plane, 
    gradient: 'from-sky-600 via-blue-500 to-indigo-500',
    iconColor: 'text-white/90'
  },
  'Government': { 
    icon: Landmark, 
    gradient: 'from-blue-700 via-indigo-600 to-purple-600',
    iconColor: 'text-white/90'
  },
  'Logistics': { 
    icon: Truck, 
    gradient: 'from-orange-600 via-amber-500 to-yellow-500',
    iconColor: 'text-white/90'
  },
  'Consulting': { 
    icon: Briefcase, 
    gradient: 'from-violet-600 via-purple-500 to-fuchsia-500',
    iconColor: 'text-white/90'
  },
  'Infrastructure': { 
    icon: Building2, 
    gradient: 'from-gray-700 via-slate-600 to-zinc-600',
    iconColor: 'text-white/90'
  },
  'Utilities': { 
    icon: Zap, 
    gradient: 'from-cyan-600 via-teal-500 to-emerald-500',
    iconColor: 'text-white/90'
  },
  'Retail': { 
    icon: Building, 
    gradient: 'from-pink-600 via-rose-500 to-red-500',
    iconColor: 'text-white/90'
  },
  'General': { 
    icon: Newspaper, 
    gradient: 'from-slate-600 via-gray-500 to-zinc-500',
    iconColor: 'text-white/90'
  }
};

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

// Date presets
const datePresets = [
  { id: 'any', en: 'Any Date', ar: 'أي تاريخ', days: null },
  { id: 'today', en: 'Today', ar: 'اليوم', days: 0 },
  { id: 'week', en: 'Past Week', ar: 'الأسبوع الماضي', days: 7 },
  { id: 'month', en: 'Past Month', ar: 'الشهر الماضي', days: 30 },
  { id: '3months', en: 'Past 3 Months', ar: 'آخر 3 أشهر', days: 90 },
  { id: '6months', en: 'Past 6 Months', ar: 'آخر 6 أشهر', days: 180 },
  { id: 'older', en: 'More than 6 Months', ar: 'أكثر من 6 أشهر', days: -180 },
  { id: 'custom', en: 'Custom Range', ar: 'نطاق مخصص', days: 'custom' }
];

// Date Filter Component
const DateFilter = ({ selectedPreset, onPresetChange, dateRange, onDateRangeChange, language, isRTL, testId }) => {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(selectedPreset === 'custom');

  const handlePresetSelect = (preset) => {
    onPresetChange(preset.id);
    if (preset.id === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      onDateRangeChange({ from: null, to: null });
      setOpen(false);
    }
  };

  const formatDateLabel = () => {
    if (selectedPreset === 'custom' && dateRange.from) {
      const fromStr = dateRange.from.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric' });
      const toStr = dateRange.to 
        ? dateRange.to.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric' })
        : language === 'ar' ? 'الآن' : 'Now';
      return `${fromStr} - ${toStr}`;
    }
    const preset = datePresets.find(p => p.id === selectedPreset);
    return preset ? (language === 'ar' ? preset.ar : preset.en) : (language === 'ar' ? 'أي تاريخ' : 'Any Date');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-11 px-3 justify-between min-w-[160px]"
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[120px]">
              {formatDateLabel()}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Presets */}
          <div className="border-r">
            <div className="p-2 border-b">
              <span className="text-xs font-medium text-muted-foreground px-2">
                {language === 'ar' ? 'اختيار سريع' : 'Quick Select'}
              </span>
            </div>
            <div className="p-1">
              {datePresets.map((preset) => (
                <div
                  key={preset.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                    selectedPreset === preset.id ? 'bg-primary/10 text-primary' : ''
                  }`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  {selectedPreset === preset.id && <Check className="w-4 h-4" />}
                  <span className={`text-sm ${selectedPreset === preset.id ? 'font-medium' : ''}`}>
                    {language === 'ar' ? preset.ar : preset.en}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Custom Date Range */}
          {showCustom && (
            <div className="p-3">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                {language === 'ar' ? 'اختر النطاق' : 'Select Range'}
              </div>
              <CalendarComponent
                mode="range"
                selected={dateRange}
                onSelect={(range) => {
                  onDateRangeChange(range || { from: null, to: null });
                }}
                numberOfMonths={1}
                disabled={(date) => date > new Date()}
              />
              <div className="flex gap-2 mt-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => {
                    setShowCustom(false);
                    onPresetChange('any');
                    onDateRangeChange({ from: null, to: null });
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1"
                  onClick={() => setOpen(false)}
                  disabled={!dateRange.from}
                >
                  {language === 'ar' ? 'تطبيق' : 'Apply'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Multi-select filter component
const MultiSelectFilter = ({ label, icon: Icon, options, selected, onChange, language, isRTL, testId, onLockedClick }) => {
  const [open, setOpen] = useState(false);
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectAll = () => {
    onChange(options.map(o => o.value));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-11 px-3 justify-between min-w-[160px]"
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[100px]">
              {selected.length === 0 
                ? label 
                : selected.length === 1 
                  ? options.find(o => o.value === selected[0])?.label || selected[0]
                  : `${selected.length} ${language === 'ar' ? 'محدد' : 'selected'}`
              }
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <div className="p-2 border-b flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs">
            {language === 'ar' ? 'تحديد الكل' : 'Select All'}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
            {language === 'ar' ? 'مسح' : 'Clear'}
          </Button>
        </div>
        <ScrollArea className="h-[250px]">
          <div className="p-2 space-y-1">
            {options.map((option) => (
              <div
                key={option.value}
                className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                  option.locked ? 'opacity-70' : selected.includes(option.value) ? 'bg-primary/10' : ''
                }`}
                onClick={() => (option.locked ? onLockedClick?.(option.value) : toggleOption(option.value))}
              >
                <Checkbox
                  checked={!option.locked && selected.includes(option.value)}
                  onCheckedChange={() => (option.locked ? onLockedClick?.(option.value) : toggleOption(option.value))}
                  disabled={option.locked}
                  className="pointer-events-none"
                />
                {option.icon && <span>{option.icon}</span>}
                <span className="text-sm flex-1">{option.label}</span>
                {option.locked && (
                  <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 whitespace-nowrap" data-testid={`locked-country-${option.value}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    {language === 'ar' ? 'ستاندرد' : 'Standard'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        {selected.length > 0 && (
          <div className="p-2 border-t">
            <div className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((val) => {
                const opt = options.find(o => o.value === val);
                return (
                  <Badge key={val} variant="secondary" className="text-xs">
                    {opt?.icon} {opt?.label?.slice(0, 10)}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={(e) => { e.stopPropagation(); toggleOption(val); }}
                    />
                  </Badge>
                );
              })}
              {selected.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{selected.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// NewsCard Component with expandable AI summary and aligned sections
const NewsCard = ({ item, isRTL, txt, language, formatDate, formatTimeAgo, extractLeads, extractingLeads, newsLeads, isAdmin, onDelete, deletingNews, onGenerateImage, generatingImage, onEdit, token, canLink, onRefresh, isFavorite, onFavoriteToggle, onFilterByIndustry, onTakedown, onUploadImage }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  // WO-27 — the prescribed disclosure, opened from the tag on this card.
  const [showAiNotice, setShowAiNotice] = useState(false);
  // WO-30 — true once the card's picture has failed to load, so the
  // "AI illustration" mark never sits on top of the sector placeholder.
  const [imageFailed, setImageFailed] = useState(false);

  // WO-26 — the publisher's own name is what a subscriber sees, never our
  // internal "Google News - ..." label, and the headline is shown without the
  // " - Publisher" tail that Google appends. Strings A2, A6 and A1 are BILAL's
  // prescribed wording (work order of 18/09/2026) and are displayed as written.
  const AI_BADGE = language === 'ar' ? 'ملخّص بالذكاء الاصطناعي' : 'AI summary';
  const AI_NOTICE = language === 'ar' ? 'مُلخّص أُنشئ آليًا بواسطة الذكاء الاصطناعي استنادًا إلى عنوان الناشر. لم يخضع لمراجعة تحريرية وقد يحتوي على أخطاء — يُرجى الرجوع إلى المقال الأصلي.' : 'Summary generated automatically by artificial intelligence from the publisher’s headline. It has not been reviewed by an editor and may contain errors — please read the original article.';
  // WO-27 — the same fact in the space of a tag. It is a shortening of A1, not
  // a replacement for it: A1 itself stays above the list, on the article page,
  // in the API and in the CRM note, and opens from the tag on every card.
  const AI_SHORT = language === 'ar' ? 'لم يخضع لمراجعة تحريرية' : 'not reviewed by an editor';
  const PUBLISHER_FALLBACK = language === 'ar' ? 'تجميع صحفي' : 'Press aggregation';
  // WO-30 — a picture WE made must never pass for a photograph of the event.
  // "ai_generated" is a picture from the Generate Image button (700 of them on
  // 19/09/2026); "uploaded_ai" is one uploaded and declared as AI-generated.
  // Publishers' own photographs ("extracted", from the feed, etc.) carry no mark.
  const AI_IMAGE_SOURCES = ['ai_generated', 'uploaded_ai'];
  const isAiImage = Boolean(item.image_url) && AI_IMAGE_SOURCES.includes(item.image_source);
  const AI_IMAGE_LABEL = language === 'ar' ? 'صورة توضيحية بالذكاء الاصطناعي' : 'AI illustration';
  const AI_IMAGE_NOTICE = language === 'ar' ? 'صورة توضيحية أُنشئت بالذكاء الاصطناعي. لا تُظهر الحدث أو المكان أو الأشخاص الواردين في الخبر.' : 'Illustration created by artificial intelligence. It does not show the actual event, place or people in this story.';
  const publisherName = item.publisher_name || null;
  const sourceLine = publisherName || PUBLISHER_FALLBACK;
  const stripPublisher = (t) => {
    if (!t || !publisherName) return t || '';
    for (const sep of [' - ', ' – ', ' — ']) {
      const tail = sep + publisherName;
      if (t.endsWith(tail)) return t.slice(0, -tail.length).trim();
    }
    return t;
  };

  // WO-31 — which headline and summary a card shows.
  // In Arabic, an article the publisher wrote in Arabic shows the publisher's
  // own Arabic headline, not our English translation of it. Everything else
  // keeps the English text, because no Arabic version exists.
  // Some feeds send punctuation as HTML codes ("&#039;", "&amp;"); React shows
  // them literally, so they are turned back into the characters they stand for.
  const HAS_ARABIC = /[؀-ۿ]/;
  const decodeEntities = (s) => {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    const cp = (c, m) => (c > 0 && c < 0x110000 ? String.fromCodePoint(c) : m);
    return s
      .replace(/&#(\d+);/g, (m, n) => cp(Number(n), m))
      .replace(/&#x([0-9a-f]+);/gi, (m, h) => cp(parseInt(h, 16), m))
      .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&');
  };
  const displayTitle = decodeEntities(stripPublisher(
    language === 'ar' && HAS_ARABIC.test(item.title || '') ? item.title : (item.title_en || item.title)
  ));
  const displaySummary = decodeEntities(item.summary_en || item.summary_ai || '');
  
  // Get preview text and replace last word with "..." for truncation indicator
  const getPreviewText = (text) => {
    if (!text) return '';
    const words = text.split(/\s+/);
    // If text is short enough, return as-is (no truncation needed)
    if (words.length <= 40) return text;
    // Take first 40 words, remove the last one, and add "..."
    const truncatedWords = words.slice(0, 40);
    truncatedWords.pop(); // Remove last word to replace with "..."
    return truncatedWords.join(' ') + '...';
  };
  
  const summaryText = displaySummary;
  const previewText = getPreviewText(summaryText);
  const hasMoreContent = summaryText.split(/\s+/).length > 40;
  
  // Resolve industries array: prefer item.industries[], fallback to valid single industry/category
  const rawIndustries = (() => {
    // 1. Use industries array if present and non-empty
    if (item.industries && item.industries.length > 0) {
      return item.industries.filter(id => isValidIndustryId(id));
    }
    // 2. Fallback to single industry field if it's a valid ID
    if (item.industry && isValidIndustryId(item.industry)) return [item.industry];
    // 3. Fallback to category if it's a valid ID (not "news"/"relevant")
    if (item.category && isValidIndustryId(item.category)) return [item.category];
    // 4. No valid industry found
    return [];
  })();

  // Primary industry drives image placeholder gradient (use sector as display fallback)
  const primaryIndustryId = rawIndustries[0] || item.industry || item.category || item.sector;
  
  // Get sector config for visual styling (icons, gradients)
  const getCategoryFromIndustry = (id) => {
    if (!id) return 'General';
    const constructionIds = ['general_construction', 'civil', 'mep', 'fit_out'];
    const realEstateIds = ['mixed_use', 'commercial', 'residential'];
    const infraIds = ['utilities_infra', 'transportation', 'airports', 'maritime', 'logistics', 'datacenters'];
    const energyIds = ['oil_gas', 'renewable', 'power', 'water', 'energy_storage'];
    const specializedIds = ['hospitality', 'healthcare', 'education', 'retail', 'industrial', 'technology', 'cultural', 'defense'];
    
    if (constructionIds.includes(id)) return 'Construction';
    if (realEstateIds.includes(id)) return 'Real Estate';
    if (infraIds.includes(id)) return 'Infrastructure';
    if (energyIds.includes(id)) return 'Energy';
    if (id === 'hospitality' || id === 'cultural') return 'Tourism';
    if (id === 'healthcare') return 'Healthcare';
    if (id === 'education') return 'Education';
    if (id === 'technology' || id === 'datacenters') return 'Technology';
    if (id === 'defense') return 'Defense';
    if (id === 'finance' || id === 'gov_adm') return 'Government';
    if (specializedIds.includes(id)) return 'Manufacturing';
    // Handle title-case legacy sector values
    const idLower = id.toLowerCase();
    if (idLower.includes('construction') || idLower.includes('civil') || idLower.includes('building')) return 'Construction';
    if (idLower.includes('oil') || idLower.includes('gas') || idLower.includes('energy') || idLower.includes('power') || idLower.includes('renewable')) return 'Energy';
    if (idLower.includes('real estate') || idLower.includes('residential') || idLower.includes('commercial')) return 'Real Estate';
    if (idLower.includes('transport') || idLower.includes('road') || idLower.includes('airport') || idLower.includes('port')) return 'Transportation';
    if (idLower.includes('tech') || idLower.includes('it') || idLower.includes('digital')) return 'Technology';
    if (idLower.includes('health') || idLower.includes('medical')) return 'Healthcare';
    if (idLower.includes('hotel') || idLower.includes('tourism') || idLower.includes('hospitality')) return 'Tourism';
    if (idLower.includes('education') || idLower.includes('school') || idLower.includes('university')) return 'Education';
    if (idLower.includes('defense') || idLower.includes('military')) return 'Defense';
    return 'General';
  };
  
  const sectorCategory = getCategoryFromIndustry(primaryIndustryId);
  const sectorStyle = sectorConfig[sectorCategory] || sectorConfig['General'];
  const SectorIcon = sectorStyle.icon;
  
  return (
    <Card 
      className="group relative border border-transparent bg-card/50 backdrop-blur-sm card-hover card-glow overflow-hidden flex flex-col"
      data-testid={`news-card-${item._id}`}
    >
      {/* Hover highlight overlay */}
      <div className="card-hover-highlight" />
      
      {/* News Image - Fixed height with sector-specific placeholder */}
      <div className="relative h-48 w-full overflow-hidden bg-muted flex-shrink-0">
        {item.image_url ? (
          <img 
            src={item.image_url} 
            alt={isAiImage ? `${AI_IMAGE_LABEL}: ${item.title}` : item.title}
            className="w-full h-full object-cover card-image-zoom"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
              setImageFailed(true);
            }}
          />
        ) : null}
        {/* Sector-specific placeholder - clean modern design */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br ${sectorStyle.gradient} flex flex-col items-center justify-center ${item.image_url ? 'hidden' : 'flex'}`}
          style={{ display: item.image_url ? 'none' : 'flex' }}
        >
          <SectorIcon className={`w-20 h-20 ${sectorStyle.iconColor} drop-shadow-lg`} />
        </div>
        
        {/* Source badge overlay — WO-30: one row across the top of the picture,
            publisher on one side and the "AI illustration" mark on the other.
            The mark never shrinks; a long publisher name is shortened instead. */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 pointer-events-none">
          <div className="flex gap-2 min-w-0 pointer-events-auto">
            {item.ref_id && (
              <Badge className="bg-primary/80 backdrop-blur-sm text-white border-0 font-mono text-xs flex-shrink-0" data-testid={`news-ref-id-${item._id}`}>
                {item.ref_id}
              </Badge>
            )}
            <Badge className="bg-black/60 backdrop-blur-sm text-white border-0 font-medium text-xs min-w-0" data-testid={`news-publisher-${item._id}`} title={sourceLine}>
              <span className="truncate" dir="auto">{sourceLine}</span>
            </Badge>
          </div>
          {/* WO-30 — the "AI illustration" mark. On the picture itself, always
              visible, never behind a click: a subscriber must know before
              reading the headline that this is not a photograph of the event. */}
          {isAiImage && !imageFailed && (
            <div
              className="flex-shrink-0 pointer-events-auto"
              data-testid={`ai-image-mark-${item._id}`}
              title={AI_IMAGE_NOTICE}
              aria-label={AI_IMAGE_NOTICE}
              role="note"
            >
              <Badge className="bg-black/70 backdrop-blur-sm text-white border border-white/30 font-semibold text-xs gap-1 whitespace-nowrap">
                <Sparkles className="w-3 h-3 flex-shrink-0" />
                {AI_IMAGE_LABEL}
              </Badge>
            </div>
          )}
        </div>
        
        {/* Industry badges at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
              {rawIndustries.length > 0 ? rawIndustries.slice(0, 3).map((indId) => (
                <Badge
                  key={indId}
                  className="bg-white/20 backdrop-blur-sm text-white border-0 font-semibold text-xs cursor-pointer hover:bg-white/35 transition-colors"
                  onClick={(e) => { e.stopPropagation(); onFilterByIndustry && onFilterByIndustry(indId); }}
                  data-testid={`news-industry-badge-${indId}`}
                  title={`Filter by: ${getIndustryLabel(indId, language)}`}
                >
                  <Factory className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate max-w-[90px]">{getIndustryLabel(indId, language)}</span>
                </Badge>
              )) : (
                <Badge className="bg-white/20 backdrop-blur-sm text-white border-0 font-semibold text-xs opacity-60">
                  <Factory className="w-3 h-3 mr-1" />
                  {language === 'ar' ? 'غير مصنف' : 'Unclassified'}
                </Badge>
              )}
            </div>
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-0 font-medium text-xs flex-shrink-0">
              {countryFlags[item.country]} {countryNames[language]?.[item.country] || item.country}
            </Badge>
          </div>
        </div>
      </div>

      <CardContent className="p-4 flex flex-col flex-grow">
        {/* Title - Fixed height for 2 lines */}
        <div className="h-14 mb-2 flex-shrink-0">
          {/* WO-31 — dir="auto": English text on the Arabic screen keeps its own
              direction, so punctuation and "..." land at the right end. */}
          <h3 dir="auto" className={`text-lg font-bold line-clamp-2 group-hover:text-primary transition-colors`}>
            {displayTitle}
          </h3>
        </div>
        
        {/* Published Date - Fixed height */}
        <div className={`h-5 flex items-center gap-2 text-xs text-muted-foreground mb-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
          <Calendar className="w-3 h-3" />
          <span data-testid={`news-date-${item._id}`}>{formatDate(item.published_at, item.timezone)}</span>
        </div>
        
        {/* Created By Badge - Show who created this news */}
        <div className={`h-5 flex items-center gap-2 text-xs text-muted-foreground mb-3 flex-shrink-0 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
          <User className="w-3 h-3" />
          <span className="text-primary/80 font-medium" data-testid={`news-creator-${item._id}`}>
            {!item.created_by || item.created_by === 'system'
              ? 'System'
              : typeof item.created_by === 'object' 
                ? (item.created_by.role === 'admin' ? 'Admin' : item.created_by.name || item.created_by.email)
                : (item.created_by === 'admin' ? 'Admin' : item.created_by)
            }
          </span>
        </div>

        {/* AI Summary with expandable content - Fixed height when collapsed for alignment */}
        <div className={`p-3 rounded-lg bg-primary/5 border border-primary/10 mb-4 flex-shrink-0 ${!isExpanded ? 'h-[200px]' : ''} flex flex-col`}>
          {/* WO-27 — one readable tag instead of a paragraph repeated on every
              card. The label is still on every card and still says the two
              things that matter: the text is machine-written, and no editor
              checked it. The full prescribed wording is one click away here,
              and permanently visible above the list. */}
          <button
            type="button"
            onClick={() => setShowAiNotice(v => !v)}
            aria-expanded={showAiNotice}
            title={AI_NOTICE}
            className="flex items-center gap-1.5 text-xs text-primary font-medium mb-2 self-start text-left hover:underline"
            data-testid={`ai-badge-${item._id}`}
          >
            <Sparkles className="w-3 h-3 flex-shrink-0" />
            <span>{AI_BADGE}</span>
            <span className="font-normal text-muted-foreground">· {AI_SHORT}</span>
            <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${showAiNotice ? 'rotate-180' : ''}`} />
          </button>
          {showAiNotice && (
            <p className="mb-2 text-[11px] leading-snug text-muted-foreground/90" data-testid={`ai-notice-${item._id}`}>
              {AI_NOTICE}
            </p>
          )}
          <div className="text-sm text-muted-foreground leading-relaxed flex-grow overflow-hidden">
            <p dir="auto">
              {isExpanded ? summaryText : previewText}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 h-7 px-2 text-xs text-primary hover:text-primary/80 hover:bg-primary/10 self-start"
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid={`read-more-summary-${item._id}`}
          >
            {isExpanded ? (
              <>
                <ChevronDown className="w-3 h-3 mr-1 rotate-180" />
                {language === 'ar' ? 'عرض أقل' : 'Show Less'}
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 mr-1" />
                {language === 'ar' ? 'قراءة المزيد' : 'Read More'}
              </>
            )}
          </Button>
        </div>
        
        {/* Potential Leads - Fixed height for alignment */}
        <div className="h-[72px] p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 mb-4 flex-shrink-0 overflow-hidden">
          <div className="flex items-center gap-2 text-xs text-amber-600 font-medium mb-2">
            <Zap className="w-3 h-3" />
            {language === 'ar' ? 'فرص محتملة' : 'Potential Leads'}
          </div>
          <div className="flex flex-wrap gap-2 overflow-hidden">
            {item.potential_leads?.potential_projects?.slice(0, 2).map((proj, i) => (
              <Badge key={`proj-${i}`} className="bg-purple-500/10 text-purple-600 border-purple-500/20 text-xs truncate max-w-[140px]">
                <Building2 className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate" dir="auto">{proj.name}</span>
              </Badge>
            ))}
            {item.potential_leads?.potential_companies?.slice(0, 2).map((comp, i) => (
              <Badge key={`comp-${i}`} className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs truncate max-w-[140px]">
                <Briefcase className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate" dir="auto">{comp.name}</span>
              </Badge>
            ))}
            {((item.potential_leads?.potential_projects?.length || 0) + (item.potential_leads?.potential_companies?.length || 0)) > 4 && (
              <Badge variant="outline" className="text-xs">
                +{(item.potential_leads.potential_projects?.length || 0) + (item.potential_leads.potential_companies?.length || 0) - 4} more
              </Badge>
            )}
            {/* Show "No Leads" if no potential leads found */}
            {(!item.potential_leads?.potential_projects?.length && !item.potential_leads?.potential_companies?.length) && (
              <span className="text-xs text-muted-foreground/60 italic">
                {language === 'ar' ? 'لا توجد فرص' : 'No Leads'}
              </span>
            )}
          </div>
        </div>

        {/* Linked Counters - Tenders, Projects & Companies */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <LinkedItemsPopover ids={item.linked_tender_ids} type="tenders" token={token}>
            <div className={`p-2.5 rounded-lg bg-muted/30 transition-colors ${item.linked_tender_ids?.length ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <FileText className="w-3.5 h-3.5" />
                <span className="text-xs">{language === 'ar' ? 'مناقصات' : 'tenders'}</span>
              </div>
              <p className={`text-xl font-bold ${item.linked_tender_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{item.linked_tender_ids?.length || 0}</p>
            </div>
          </LinkedItemsPopover>
          <LinkedItemsPopover ids={item.linked_project_ids} type="projects" token={token}>
            <div className={`p-2.5 rounded-lg bg-muted/30 transition-colors ${item.linked_project_ids?.length ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <Building2 className="w-3.5 h-3.5" />
                <span className="text-xs">{language === 'ar' ? 'مشاريع' : 'projects'}</span>
              </div>
              <p className={`text-xl font-bold ${item.linked_project_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{item.linked_project_ids?.length || 0}</p>
            </div>
          </LinkedItemsPopover>
          <LinkedItemsPopover ids={item.linked_company_ids} type="companies" token={token}>
            <div className={`p-2.5 rounded-lg bg-muted/30 transition-colors ${item.linked_company_ids?.length ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <Users className="w-3.5 h-3.5" />
                <span className="text-xs">{language === 'ar' ? 'شركات' : 'companies'}</span>
              </div>
              <p className={`text-xl font-bold ${item.linked_company_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{item.linked_company_ids?.length || 0}</p>
            </div>
          </LinkedItemsPopover>
        </div>

        {/* Footer Actions - Clean, professional layout */}
        <div className={`flex items-center justify-between gap-2 pt-4 border-t border-border/50 mt-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Left side - Favorite button only */}
          <div className="flex items-center gap-1">
            <FavoriteButton
              itemType="news"
              itemId={item._id}
              isFavorite={isFavorite}
              onToggle={(isFav) => onFavoriteToggle(item._id, isFav)}
              token={token}
              compact
            />
          </div>
          
          {/* Right side - Actions dropdown and Read Article */}
          <div className="flex items-center gap-1">
            {/* All actions in dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-8 px-2 text-muted-foreground hover:text-foreground"
                  data-testid={`more-actions-${item._id}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1" align="end">
                <div className="flex flex-col">
                  {/* Copy Link */}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="justify-start h-9 px-3 text-sm"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}/dashboard/news/${item._id}`;
                      await navigator.clipboard.writeText(url);
                      toast.success('Link copied!');
                    }}
                  >
                    <Link2 className="w-4 h-4 mr-2 text-blue-500" />
                    Copy Link
                  </Button>
                  
                  {/* Divider */}
                  <div className="h-px bg-border my-1" />
                  
                  {/* Extract Leads - Available to all users */}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="justify-start h-9 px-3 text-sm"
                    onClick={() => extractLeads(item)}
                    disabled={extractingLeads[item._id]}
                    data-testid={`extract-leads-${item._id}`}
                  >
                    {extractingLeads[item._id] ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4 mr-2 text-amber-500" />
                    )}
                    {newsLeads[item._id]?.length > 0 
                      ? `${newsLeads[item._id].length} ${txt.leads}` 
                      : txt.extractLeads
                    }
                  </Button>
                  
                  {/* Link to Project */}
                  {canLink && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="justify-start h-9 px-3 text-sm"
                      onClick={() => setShowLinkDialog(true)}
                      data-testid={`link-news-${item._id}`}
                    >
                      <Link2 className="w-4 h-4 mr-2 text-purple-500" />
                      {language === 'ar' ? 'إدارة الروابط' : 'Manage Links'}
                    </Button>
                  )}
                  
                  {isAdmin && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start h-9 px-3 text-sm"
                        onClick={() => onGenerateImage(item._id)}
                        disabled={generatingImage?.[item._id]}
                        data-testid={`generate-image-${item._id}`}
                      >
                        {generatingImage?.[item._id] ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-2 text-violet-500" />
                        )}
                        {language === 'ar' ? 'توليد صورة' : 'Generate Image'}
                      </Button>

                      {/* WO-28 — a picture of our own, made or owned by us.
                          Never one taken from the article: the app links to a
                          publisher's picture, it does not keep a copy. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start h-9 px-3 text-sm"
                        onClick={() => onUploadImage && onUploadImage(item)}
                        data-testid={`upload-image-${item._id}`}
                      >
                        <Plus className="w-4 h-4 mr-2 text-emerald-500" />
                        {language === 'ar' ? 'رفع صورة' : 'Upload Image'}
                      </Button>
                      
                      {/* Divider */}
                      <div className="h-px bg-border my-1" />
                      
                      {/* Edit */}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="justify-start h-9 px-3 text-sm"
                        onClick={() => onEdit(item)}
                        data-testid={`edit-news-${item._id}`}
                      >
                        <Pencil className="w-4 h-4 mr-2 text-blue-500" />
                        {txt.edit}
                      </Button>

                      {/* WO-27 — the takedown the footer promises. Delete throws
                          the article away; this records an objection and keeps
                          the evidence that we acted on it. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start h-9 px-3 text-sm"
                        onClick={() => onTakedown && onTakedown(item)}
                        data-testid={`takedown-news-${item._id}`}
                      >
                        <Shield className="w-4 h-4 mr-2 text-amber-500" />
                        {language === 'ar' ? 'اعتراض ناشر' : 'Publisher objection'}
                      </Button>

                      {/* Delete */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start h-9 px-3 text-sm text-destructive hover:text-destructive"
                        onClick={() => onDelete(item._id)}
                        data-testid={`delete-news-${item._id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {txt.delete}
                      </Button>
                    </>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Read Article - Primary CTA */}
            <Button 
              size="sm" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-3 text-xs"
              onClick={() => { const u = safeUrl(item.url); if (u) window.open(u, '_blank'); }}
              disabled={!safeUrl(item.url)}
              data-testid={`read-article-${item._id}`}
            >
              {txt.readMore}
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
      
      {/* Manage Links Dialog */}
      <ManageLinksDialog
        open={showLinkDialog}
        onOpenChange={setShowLinkDialog}
        fromType="news"
        fromId={item._id}
        fromTitle={typeof item.title === 'object' ? item.title?.en || item.title?.ar : item.title}
        currentLinks={{
          tenders:   item.linked_tender_ids   || [],
          projects:  item.linked_project_ids  || [],
          companies: item.linked_company_ids  || [],
        }}
        token={token}
        onSuccess={onRefresh}
      />
    </Card>
  );
};

export default function NewsPage() {
  const { language, isRTL } = useLanguage();
  const { user, token } = useAuth();
  const planGate = usePlanGate();
  const { allowedCountries, allCountries, upgradeUrl, planId } = useEntitlements();
  const isCountryLocked = (code) => !allCountries && !allowedCountries.includes(code);
  const handleLockedCountry = (code) => planGate.show(buildCountryGate(code, planId, upgradeUrl, language));
  const [countryGate, setCountryGate] = useState(null);
  const navigate = useNavigate();
  const { id: deepLinkId } = useParams();
  const [searchParams] = useSearchParams();
  const filterIds = (searchParams.get('filter_ids') || '').split(',').filter(Boolean);
  const isFiltered = filterIds.length > 0;
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Multi-select Filters
  const [search, setSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  // WO-26 — the publishers behind the articles, for the subscriber-facing filter.
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [linkedProjectFilter, setLinkedProjectFilter] = useState([]);
  const [linkedCompanyFilter, setLinkedCompanyFilter] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  
  // Date Filter
  const [datePreset, setDatePreset] = useState('any');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  
  // Feed sources
  const [feedSources, setFeedSources] = useState([]);
  const [userPreferences, setUserPreferences] = useState({ hidden_sources: [], favorite_sources: [] });
  const [showManageFeeds, setShowManageFeeds] = useState(false);
  
  // Leads
  const [selectedNewsForLeads, setSelectedNewsForLeads] = useState(null);
  const [newsLeads, setNewsLeads] = useState({});
  const [extractingLeads, setExtractingLeads] = useState({});
  const [showLeadsDialog, setShowLeadsDialog] = useState(false);
  
  // Custom lead dialog state
  const [showCustomLeadDialog, setShowCustomLeadDialog] = useState(false);
  const [customLeadType, setCustomLeadType] = useState('project');
  const [customLeadName, setCustomLeadName] = useState('');
  const [customLeadDescription, setCustomLeadDescription] = useState('');
  const [creatingCustomLead, setCreatingCustomLead] = useState(false);
  const [savingLead, setSavingLead] = useState({});
  const [deletingNews, setDeletingNews] = useState({});
  const [generatingImage, setGeneratingImage] = useState({});
  
  // Progress tracking for refresh
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [refreshStatus, setRefreshStatus] = useState('');
  // Live detail for article-level + per-source progress
  const [refreshDetail, setRefreshDetail] = useState(null);
  // WO-25 — the job being followed ({ id, mode }), kept in a ref as well so the
  // long-running stream loop always reads the current value.
  const [refreshJob, setRefreshJobState] = useState(null);
  const refreshJobRef = useRef(null);
  const setRefreshJob = (job) => { refreshJobRef.current = job; setRefreshJobState(job); };
  const streamAbortRef = useRef(null);
  const startingRefreshRef = useRef(false);
  const mountedRef = useRef(true);
  const fetchSeqRef = useRef(0);
  const fetchNewsRef = useRef(null);
  const [reportError, setReportError] = useState(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // WO-25 — leaving the page closes the progress connection (the job
      // itself keeps running on the server).
      mountedRef.current = false;
      if (streamAbortRef.current) streamAbortRef.current.abort();
    };
  }, []);

  // Last Refresh Report (per-source breakdown of the most recent finished job)
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // WO-27 — takedowns. The footer promises removal within 72 hours; until now
  // the only way to honour it was an API call made by hand.
  const [showTakedownDialog, setShowTakedownDialog] = useState(false);
  const [takedownItem, setTakedownItem] = useState(null);
  const [takedownScope, setTakedownScope] = useState('publisher');
  const [takedownReason, setTakedownReason] = useState('');
  const [takedownSaving, setTakedownSaving] = useState(false);
  const [suppressions, setSuppressions] = useState(null);
  // WO-28 — putting a picture of our own on a card.
  const [uploadItem, setUploadItem] = useState(null);
  const [uploadProvenance, setUploadProvenance] = useState('ai_generated');
  const [uploadNote, setUploadNote] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  // Duplicate cleanup
  const [showDedupDialog, setShowDedupDialog] = useState(false);
  const [showBackfillConfirm, setShowBackfillConfirm] = useState(false);
  const [dedupScanning, setDedupScanning] = useState(false);
  const [dedupResult, setDedupResult] = useState(null); // { cluster_count, duplicate_count, sample }
  const [dedupRemoving, setDedupRemoving] = useState(false);
  const [dedupProgress, setDedupProgress] = useState(0);
  const [dedupDone, setDedupDone] = useState(null); // { removed }
  
  // Admin dialog state
  const [showAdminNewsDialog, setShowAdminNewsDialog] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsToDelete, setNewsToDelete] = useState(null);
  
  // Use the centralized favorites hook
  const { 
    isFavorite, 
    handleFavoriteToggle, 
    filterByFavorites,
    showFavoritesOnly, 
    setShowFavoritesOnly 
  } = useFavorites('news', token);

  // BUG-L02 FIX: deep link support — scroll to / highlight the item from URL
  const deepLinkRef = useRef(null);
  useEffect(() => {
    if (!deepLinkId || !news.length) return;
    // Small delay to allow render
    setTimeout(() => {
      if (deepLinkRef.current) {
        deepLinkRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  }, [deepLinkId, news]);

  const isAdmin = user?.role === 'admin';
  const userPlan = user?.plan_id || 'free';
  const isPremiumOrEnterprise = userPlan === 'premium' || userPlan === 'enterprise';
  
  // Check if user can link news to projects
  const canLinkNews = (newsItem) => {
    if (isAdmin) return true;
    // WO-25 — news created in the app stores created_by as an object.
    const creatorId = (newsItem.created_by && typeof newsItem.created_by === 'object')
      ? newsItem.created_by.user_id
      : newsItem.created_by;
    if (isPremiumOrEnterprise && creatorId && creatorId === user?.user_id) return true;
    return false;
  };
  
  // Page size for initial load and load more
  const PAGE_SIZE = 20;

  // Calculate date filter bounds
  const getDateBounds = () => {
    if (datePreset === 'custom' && dateRange.from) {
      // WO-25 — "to" is the END of the chosen day, not its first second.
      const to = dateRange.to ? new Date(dateRange.to) : new Date();
      if (dateRange.to) to.setHours(23, 59, 59, 999);
      return {
        from: dateRange.from,
        to
      };
    }
    
    const preset = datePresets.find(p => p.id === datePreset);
    if (!preset || preset.days === null) return null;
    
    const now = new Date();
    if (preset.days === -180) {
      // More than 6 months ago
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setDate(sixMonthsAgo.getDate() - 180);
      return { from: null, to: sixMonthsAgo };
    } else if (preset.days === 0) {
      // Today
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { from: startOfDay, to: now };
    } else {
      const pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - preset.days);
      return { from: pastDate, to: now };
    }
  };

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  });

  const fetchNews = async (isLoadMore = false, forceRefresh = false) => {
    // WO-25 — only the newest request may update the list. Before, a slow
    // answer for an old filter could overwrite a newer one.
    const seq = ++fetchSeqRef.current;
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const currentPage = isLoadMore ? page + 1 : 1;
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: PAGE_SIZE.toString()
      });
      
      // Add cache-busting parameter for refresh
      if (forceRefresh) {
        params.append('_t', Date.now().toString());
      }
      
      // Server-side multi-value filters (repeat the query param)
      selectedCountries.forEach(c => params.append('country', c));
      selectedSources.forEach(s => params.append('source', s));
      selectedPublishers.forEach(p => params.append('publisher', p));
      selectedIndustries.forEach(i => params.append('industry', i));
      
      if (search) params.append('search', search);
      if (linkedProjectFilter.length > 0) linkedProjectFilter.forEach(f => params.append('project_id', f._id));
      if (linkedCompanyFilter.length > 0) linkedCompanyFilter.forEach(f => params.append('company_id', f._id));

      // WO-25 — the date filter and "hide this feed" are applied by the server,
      // so pages and counts are right. Both used to run in the browser on the
      // 20 articles already loaded.
      const dateBounds = getDateBounds();
      if (dateBounds?.from) params.append('published_from', new Date(dateBounds.from).toISOString());
      if (dateBounds?.to) params.append('published_to', new Date(dateBounds.to).toISOString());
      (userPreferences.hidden_sources || []).forEach(id => params.append('exclude_source', id));
      
      const response = await fetch(`${BACKEND_URL}/api/intelligence/news?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error(
          response.status === 401
            ? (language === 'ar' ? 'انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى.' : 'Your session has ended. Please sign in again.')
            : (language === 'ar' ? 'تعذّر تحميل الأخبار. حاول مرة أخرى.' : 'The news could not be loaded. Please try again.')
        );
      }
      
      const data = await response.json();
      if (seq !== fetchSeqRef.current) return; // a newer request has replaced this one
      const items = data.items || [];
      
      if (isLoadMore) {
        // Append new items to existing list
        setNews(prev => [...prev, ...items]);
        setPage(currentPage);
      } else {
        // Replace list with new items
        setNews(items);
        setPage(1);
      }
      
      // Check if there are more items to load
      setHasMore(data.page < data.total_pages);
      setCountryGate(data.plan_gate || null);
      setTotal(data.total);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err.message);
      if (!forceRefresh) {
        toast.error(language === 'ar' ? 'فشل تحميل الأخبار' : 'Failed to load news');
      }
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };
  
  const loadMore = () => {
    fetchNews(true);
  };
  // WO-25 — always the latest fetchNews, so a refresh that ends several minutes
  // after it started reloads the list with the filters on screen NOW.
  fetchNewsRef.current = fetchNews;

  // WO-25 — why an article was not saved, in the words the report shows.
  const SKIP_REASON_LABELS = {
    already_saved: { en: 'Already saved', ar: 'محفوظ مسبقًا' },
    older_than_last_refresh: { en: 'Older than the last refresh', ar: 'أقدم من آخر تحديث' },
    deleted_before: { en: 'Deleted earlier', ar: 'محذوف سابقًا' },
    rejected_before: { en: 'Rejected on an earlier refresh', ar: 'مرفوض في تحديث سابق' },
    not_relevant: { en: 'Not business-relevant', ar: 'غير ذي صلة بالأعمال' },
    no_summary: { en: 'Too little text to summarise', ar: 'نص غير كافٍ للتلخيص' },
    ai_error: { en: 'AI error (retried next time)', ar: 'خطأ في الذكاء الاصطناعي (يُعاد لاحقًا)' },
    left_for_next_run: { en: 'Left for the next refresh', ar: 'مؤجل للتحديث التالي' },
    too_short_in_feed: { en: 'Empty entry in the feed', ar: 'مدخل فارغ في الموجز' },
    older_than_2026: { en: 'Published before 2026', ar: 'منشور قبل 2026' },
  };
  const describeSkipReasons = (reasons) => Object.entries(reasons || {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${(SKIP_REASON_LABELS[k] || { en: k, ar: k })[language === 'ar' ? 'ar' : 'en']}: ${n}`)
    .join(' · ');
  const MODE_LABELS = {
    normal: { en: 'Refresh', ar: 'تحديث الأخبار' },
    full: { en: 'Full Re-scan', ar: 'إعادة فحص كاملة' },
    backfill: { en: 'Deep Backfill', ar: 'سحب تاريخي عميق' },
  };
  const modeLabel = (m) => (MODE_LABELS[m] || MODE_LABELS.normal)[language === 'ar' ? 'ar' : 'en'];

  const resetRefreshUi = () => {
    setRefreshProgress(null);
    setRefreshStatus('');
    setRefreshDetail(null);
    setRefreshJob(null);
  };

  // Refresh news using a SERVER-SIDE background job. The worker keeps running
  // even if this tab is closed; reopening the page reattaches via /active.
  const refreshNewsWithIngestion = async ({ full = false, backfill = false } = {}) => {
    // WO-25 — a ref, not state: two quick clicks happen before React re-renders.
    if (startingRefreshRef.current || (refreshProgress !== null && refreshProgress < 100)) {
      toast.info(language === 'ar' ? 'جارٍ تحديث الأخبار بالفعل…' : 'A news refresh is already in progress…');
      return;
    }
    startingRefreshRef.current = true;
    setRefreshProgress(0);
    setRefreshStatus(language === 'ar' ? 'جارٍ الاتصال...' : 'Connecting...');

    try {
      const params = new URLSearchParams({ enable_ai: 'true' });
      if (backfill) params.set('backfill', 'true');
      else if (full) params.set('full', 'true');
      const startResp = await fetch(`${BACKEND_URL}/api/intelligence/news/ingest-start?${params.toString()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!startResp.ok) {
        let detail = '';
        try { const body = await startResp.json(); detail = typeof body?.detail === 'string' ? body.detail : ''; } catch { /* no body */ }
        throw new Error(detail || (language === 'ar' ? 'تعذّر بدء التحديث' : 'The server did not start the refresh'));
      }
      const outcome = await startResp.json();
      const jobId = outcome.job_id;
      const mode = outcome.mode || 'normal';
      setRefreshJob({ id: jobId, mode });

      if (outcome.already_running) {
        // WO-25 — say what really happened instead of "Refresh started".
        toast.info(
          language === 'ar'
            ? `هناك تحديث قيد التشغيل بالفعل (${modeLabel(mode)}). تتم متابعته؛ لم يبدأ ${modeLabel(outcome.requested_mode)}.`
            : `A ${modeLabel(mode)} is already running. Showing its progress; your ${modeLabel(outcome.requested_mode)} was not started.`
        );
      } else {
        toast.success(
          language === 'ar'
            ? `بدأ ${modeLabel(mode)} في الخلفية — يمكنك مغادرة الصفحة`
            : `${modeLabel(mode)} started in the background — you can leave this page`
        );
      }

      startingRefreshRef.current = false;
      // Subscribe to progress (closing this just unsubscribes — worker keeps going)
      await streamIngestJob(jobId);
    } catch (err) {
      console.error('Failed to start refresh:', err);
      toast.error(
        language === 'ar' ? 'فشل بدء التحديث' : 'Could not start the refresh',
        { description: err?.message }
      );
      resetRefreshUi();
    } finally {
      startingRefreshRef.current = false;
    }
  };

  // WO-25 — Stop: the server already supported cancelling; nothing called it.
  const stopRefresh = async () => {
    if (!refreshJob?.id) return;
    try {
      const resp = await fetch(`${BACKEND_URL}/api/intelligence/news/ingest-job/${refreshJob.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(String(resp.status));
      setRefreshStatus(language === 'ar' ? 'جارٍ الإيقاف…' : 'Stopping…');
    } catch {
      toast.error(language === 'ar' ? 'تعذّر إيقاف التحديث' : 'Could not stop the refresh. Please try again.');
    }
  };

  // Apply one progress/complete message from the server. Returns true when the
  // job has finished (the caller stops listening).
  const applyJobEvent = async (data) => {
    if (data.event === 'progress') {
      const pct = typeof data.progress_pct === 'number'
        ? Math.min(99, data.progress_pct)
        : (data.total_sources > 0 && data.current_source
            ? Math.min(99, Math.round((data.current_source.index / data.total_sources) * 100))
            : 0);
      setRefreshProgress(pct);
      setRefreshDetail({
        totals: data.totals || {},
        activeSources: data.active_sources || [],
        perSource: data.per_source || [],
        sourcesDone: data.sources_done || 0,
        totalSources: data.total_sources || 0,
      });
      const done = data.sources_done || 0;
      const totalSrc = data.total_sources || 0;
      setRefreshStatus(
        language === 'ar'
          ? `${modeLabel(refreshJobRef.current?.mode)}: اكتمل ${done} من ${totalSrc} مصدرًا`
          : `${modeLabel(refreshJobRef.current?.mode)}: ${done} of ${totalSrc} sources done`
      );
      return false;
    }
    if (data.event === 'complete') {
      if (data.status === 'cancelled') {
        toast.info(language === 'ar' ? 'تم إيقاف التحديث. ما حُفظ قبل الإيقاف باقٍ.' : 'Refresh stopped. Articles saved before the stop are kept.');
        resetRefreshUi();
        await fetchNewsRef.current?.(false, true);
        return true;
      }
      if (data.status === 'failed') {
        const why = (data.errors || [])[0];
        toast.error(
          language === 'ar' ? 'توقف التحديث قبل أن يكتمل' : 'The refresh stopped before it finished',
          { description: why || (language === 'ar' ? 'راجع تقرير آخر تحديث.' : 'See the Last Refresh Report.') }
        );
        resetRefreshUi();
        await fetchNewsRef.current?.(false, true);
        return true;
      }
      setRefreshProgress(100);
      const t = data.totals || {};
      const breakdown = describeSkipReasons(t.skip_reasons);
      setRefreshStatus(
        language === 'ar'
          ? `اكتمل: ${t.new || 0} جديد`
          : `Complete: ${t.new || 0} new`
      );
      if ((t.new || 0) > 0) {
        toast.success(
          language === 'ar' ? `تمت إضافة ${t.new} خبرًا جديدًا` : `${t.new} new articles added`,
          { description: breakdown || undefined }
        );
      } else {
        toast.info(
          language === 'ar' ? 'لا توجد أخبار جديدة' : 'No new articles',
          { description: breakdown || undefined }
        );
      }
      await fetchNewsRef.current?.(false, true);
      setTimeout(resetRefreshUi, 3000);
      return true;
    }
    if (data.event === 'error') {
      // WO-25 — this used to be thrown inside a catch that ignored it.
      toast.error(
        language === 'ar' ? 'تعذّرت متابعة التحديث' : 'Could not follow the refresh',
        { description: data.message }
      );
      resetRefreshUi();
      return true;
    }
    return false;
  };

  // Subscribe to a background ingestion job's SSE stream.
  // Detaching is safe — server keeps running.
  // WO-25 — if the connection drops without a final message (proxy timeout,
  // laptop asleep, redeploy), ask the server where the job is and reconnect,
  // instead of freezing with every refresh button disabled.
  const streamIngestJob = async (jobId) => {
    const MAX_RECONNECTS = 20;
    for (let attempt = 0; attempt <= MAX_RECONNECTS; attempt += 1) {
      if (streamAbortRef.current) streamAbortRef.current.abort();
      const controller = new AbortController();
      streamAbortRef.current = controller;
      try {
        const response = await fetch(`${BACKEND_URL}/api/intelligence/news/ingest-job/${jobId}/stream`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/event-stream' },
          signal: controller.signal,
        });
        if (response.status === 404) {
          await applyJobEvent({ event: 'error', message: language === 'ar' ? 'لم يعد التحديث موجودًا.' : 'That refresh no longer exists.' });
          return;
        }
        if (!response.ok) throw new Error(`stream HTTP ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue; // ": keep-alive" lines are skipped here
            let data;
            try {
              data = JSON.parse(line.slice(6));
            } catch {
              continue; // a malformed line is ignored; a real error event is not
            }
            if (await applyJobEvent(data)) return;
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return; // page left or a newer stream took over
        console.warn('Job stream dropped:', err);
      }

      // The stream ended without a final message: ask for the job's state.
      try {
        const snap = await fetch(`${BACKEND_URL}/api/intelligence/news/ingest-job/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (snap.status === 404) {
          await applyJobEvent({ event: 'error', message: language === 'ar' ? 'لم يعد التحديث موجودًا.' : 'That refresh no longer exists.' });
          return;
        }
        if (snap.ok) {
          const job = await snap.json();
          if (['completed', 'failed', 'cancelled'].includes(job.status)) {
            await applyJobEvent({
              event: 'complete',
              status: job.status,
              totals: job.totals || {},
              errors: job.errors || [],
              per_source: job.per_source || [],
            });
            return;
          }
          hydrateFromJob(job);
        }
      } catch {
        /* network still down: wait and retry */
      }
      if (!mountedRef.current) return;
      setRefreshStatus(language === 'ar' ? 'انقطع الاتصال — جارٍ إعادة الاتصال…' : 'Connection dropped — reconnecting…');
      await new Promise((r) => setTimeout(r, Math.min(30000, 2000 * (attempt + 1))));
      if (!mountedRef.current) return;
    }
    toast.error(
      language === 'ar'
        ? 'تعذّر الاتصال بالتحديث. يستمر العمل على الخادم؛ افتح تقرير آخر تحديث لاحقًا.'
        : 'Could not reconnect to the refresh. It keeps running on the server; check the Last Refresh Report later.'
    );
    resetRefreshUi();
  };

  // True while a refresh is actively running (used to lock the Refresh button).
  const isRefreshing = refreshProgress !== null && refreshProgress < 100;

  // Fetch the per-source breakdown of the most recent finished refresh.
  const fetchLastReport = async () => {
    setLoadingReport(true);
    setReportError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/intelligence/news/ingest-job/last`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const { job } = await resp.json();
        setLastReport(job);
      } else {
        setLastReport(null);
        setReportError(language === 'ar' ? 'تعذّر تحميل التقرير. حاول مرة أخرى.' : 'The report could not be loaded. Please try again.');
      }
    } catch {
      setLastReport(null);
      setReportError(language === 'ar' ? 'تعذّر تحميل التقرير. تحقق من الاتصال وحاول مرة أخرى.' : 'The report could not be loaded. Check your connection and try again.');
    } finally {
      setLoadingReport(false);
    }
  };

  const openReportDialog = () => {
    setShowReportDialog(true);
    fetchLastReport();
  };

  // Run the duplicate-cleanup SSE stream. dryRun=true => preview only.
  const runDedup = async (dryRun) => {
    if (dryRun) {
      setDedupScanning(true);
      setDedupResult(null);
      setDedupDone(null);
    } else {
      setDedupRemoving(true);
      setDedupProgress(0);
    }
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/intelligence/news/dedup-stream?dry_run=${dryRun ? 'true' : 'false'}`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/event-stream' } }
      );
      if (!resp.ok) throw new Error('Failed to start cleanup');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.event === 'analyzed') {
              setDedupResult({
                cluster_count: data.cluster_count,
                duplicate_count: data.duplicate_count,
                sample: data.sample || [],
              });
            } else if (data.event === 'progress') {
              setDedupProgress(data.progress || 0);
            } else if (data.event === 'complete') {
              if (!dryRun) {
                setDedupDone({ removed: data.removed || 0 });
                setDedupProgress(100);
                if (data.removed > 0) {
                  toast.success(
                    language === 'ar'
                      ? `تمت إزالة ${data.removed} خبرًا مكررًا`
                      : `Removed ${data.removed} duplicate articles`
                  );
                  fetchNews(false, true);
                } else {
                  toast.info(language === 'ar' ? 'لا توجد تكرارات' : 'No duplicates found');
                }
              }
            } else if (data.event === 'error') {
              throw new Error(data.message);
            }
          } catch {
            /* ignore malformed sse line */
          }
        }
      }
    } catch (err) {
      console.error('Dedup error:', err);
      toast.error(language === 'ar' ? 'فشلت عملية التنظيف' : 'Cleanup failed');
    } finally {
      setDedupScanning(false);
      setDedupRemoving(false);
    }
  };

  const openDedupDialog = () => {
    setShowDedupDialog(true);
    setDedupResult(null);
    setDedupDone(null);
    setDedupProgress(0);
    runDedup(true); // auto-scan on open
  };

  // Filter news by favorites if toggle is on
  const displayedNews = filterByFavorites(news);

  // WO-26 — the publishers behind the stored articles, for the filter above.
  const fetchPublishers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/news/publishers`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPublishers(data.publishers || []);
      }
    } catch (err) {
      console.error('Failed to fetch publishers:', err);
    }
  };

  // WO-27 — the takedown route, from the objection to the evidence.
  const fetchSuppressions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/news/admin/suppressions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) setSuppressions(await response.json());
    } catch (err) {
      console.error('Failed to fetch suppressions:', err);
    }
  };

  const openTakedown = (item) => {
    setTakedownItem(item || null);
    setTakedownScope(item && item.publisher_name ? 'publisher' : 'article');
    setTakedownReason('');
    setSuppressions(null);
    setShowTakedownDialog(true);
    fetchSuppressions();
  };

  const submitTakedown = async () => {
    if (!takedownItem) return;
    const value = takedownScope === 'publisher'
      ? (takedownItem.publisher_name || '')
      : (takedownItem._id || takedownItem.id || '');
    if (!value) {
      toast.error(language === 'ar' ? 'لا يوجد ناشر محدد لهذا المقال.' : 'This article has no publisher to object for.');
      return;
    }
    setTakedownSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/news/admin/suppress`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: takedownScope, value, reason: takedownReason })
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      toast.success(
        language === 'ar'
          ? `تمت إزالة ${data.suppressed ?? ''} مقالًا من العرض.`
          : `${data.suppressed ?? ''} article(s) removed from view.`
      );
      setTakedownItem(null);
      await fetchSuppressions();
      await fetchPublishers();
      fetchNews();
    } catch (err) {
      console.error('Takedown failed:', err);
      toast.error(language === 'ar' ? 'تعذّر تنفيذ الاعتراض.' : 'The objection could not be recorded.');
    } finally {
      setTakedownSaving(false);
    }
  };

  // WO-28 — upload a picture we own. The server refuses anything that is not
  // declared as ours, and records what was declared.
  const openUpload = (item) => {
    setUploadItem(item);
    setUploadProvenance('ai_generated');
    setUploadNote('');
    setUploadFile(null);
  };

  const submitUpload = async () => {
    if (!uploadItem || !uploadFile) return;
    setUploadBusy(true);
    try {
      const body = new FormData();
      body.append('file', uploadFile);
      body.append('provenance', uploadProvenance);
      body.append('note', uploadNote);
      // Only the token: getAuthHeaders() sets Content-Type: application/json,
      // which would replace the multipart boundary the browser generates for
      // FormData and the server would answer 422.
      const response = await fetch(
        `${BACKEND_URL}/api/admin/news/${uploadItem._id}/upload-image`,
        { method: 'POST', headers: { Authorization: token ? `Bearer ${token}` : '' }, body }
      );
      if (!response.ok) {
        let detail = '';
        try { detail = (await response.json()).detail || ''; } catch (e) { /* ignore */ }
        throw new Error(detail || 'upload failed');
      }
      toast.success(language === 'ar' ? 'تم رفع الصورة.' : 'Image uploaded.');
      setUploadItem(null);
      setUploadFile(null);
      fetchNews();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.message || (language === 'ar' ? 'تعذّر رفع الصورة.' : 'The image could not be uploaded.'));
    } finally {
      setUploadBusy(false);
    }
  };

  const liftSuppression = async (rule) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/news/admin/unsuppress`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: rule.scope, value: rule.value })
      });
      if (!response.ok) throw new Error(await response.text());
      toast.success(language === 'ar' ? 'أُعيد المحتوى.' : 'Content restored.');
      await fetchSuppressions();
      await fetchPublishers();
      fetchNews();
    } catch (err) {
      console.error('Lift failed:', err);
      toast.error(language === 'ar' ? 'تعذّر إعادة المحتوى.' : 'The content could not be restored.');
    }
  };

  const fetchFeedSources = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/news/sources`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Map sources to include _id field for compatibility
        const sourcesWithId = (data.sources || []).map(s => ({
          ...s,
          _id: s.id || s.name.toLowerCase().replace(/\s+/g, '_')
        }));
        setFeedSources(sourcesWithId);
      }
    } catch (err) {
      console.error('Failed to fetch feed sources:', err);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/feeds/preferences`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUserPreferences(data);
      }
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    }
  };

  useEffect(() => {
    fetchFeedSources();
    fetchPublishers();
    fetchUserPreferences();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Hydrate the progress UI immediately from a job doc (reattach path), so the
  // running tally/details show right away without waiting for the first SSE tick.
  const hydrateFromJob = (job) => {
    if (!job) return;
    setRefreshProgress(typeof job.progress_pct === 'number' ? Math.min(99, job.progress_pct) : 0);
    setRefreshDetail({
      totals: job.totals || {},
      activeSources: job.active_sources || [],
      perSource: job.per_source || [],
      sourcesDone: job.sources_done || 0,
      totalSources: job.total_sources || 0,
    });
  };

  // Reattach to a still-running ingestion job (admin only). This MUST wait for
  // auth to load — running once on mount with a null token silently fails and
  // makes the refresh look like it "restarted from scratch". Depending on
  // role+token guarantees we reattach the moment auth is ready.
  const reattachedRef = useRef(false);
  useEffect(() => {
    if (user?.role !== 'admin' || !token || reattachedRef.current) return;
    reattachedRef.current = true;
    (async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}/api/intelligence/news/ingest-job/active`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const { job } = await resp.json();
        if (job && (job.status === 'running' || job.status === 'queued')) {
          toast.info(
            language === 'ar'
              ? 'تم العثور على تحديث جارٍ — جارٍ المتابعة'
              : 'Found an ongoing refresh — re-attaching',
            { description: `job ${String(job._id).slice(0, 8)}…` }
          );
          hydrateFromJob(job);
          setRefreshJob({
            id: job._id,
            mode: job.mode || (job.backfill_mode ? 'backfill' : (job.full_rescan ? 'full' : 'normal')),
          });
          streamIngestJob(job._id);
        }
      } catch {
        /* ignore */
      }
    })();
  }, [user?.role, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isFiltered) return;
    fetchNews(false);
  }, [isFiltered, selectedCountries, selectedSources, selectedPublishers, selectedIndustries, search, datePreset, dateRange, userPreferences.hidden_sources, linkedProjectFilter, linkedCompanyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isFiltered || !token) return;
    const loadFiltered = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          filterIds.map(id =>
            fetch(`${BACKEND_URL}/api/intelligence/news/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.ok ? r.json() : null)
          )
        );
        const valid = results.filter(Boolean);
        setNews(valid);
        setTotal(valid.length);
      } finally {
        setLoading(false);
      }
    };
    loadFiltered();
  }, [filterIds.join(','), token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    // fetchNews will be triggered by useEffect due to search change
  };

  const toggleHideFeed = async (sourceId, sourceName) => {
    const isHidden = userPreferences.hidden_sources.includes(sourceId);
    const endpoint = isHidden ? 'unhide' : 'hide';
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/feeds/preferences/${endpoint}/${sourceId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setUserPreferences(prev => ({
          ...prev,
          hidden_sources: isHidden 
            ? prev.hidden_sources.filter(id => id !== sourceId)
            : [...prev.hidden_sources, sourceId]
        }));
        toast.success(isHidden ? `${sourceName} is now visible` : `${sourceName} hidden from feed`);
      }
    } catch (err) {
      toast.error('Failed to update preference');
    }
  };

  const extractLeads = async (newsItem) => {
    setExtractingLeads(prev => ({ ...prev, [newsItem._id]: true }));
    
    try {
      // Get leads from news (extracts with AI if not already stored)
      const response = await fetch(`${BACKEND_URL}/api/opportunities/news/${newsItem._id}/leads`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Store leads in state
        setNewsLeads(prev => ({ ...prev, [newsItem._id]: data.leads }));
        
        // Open dialog to show leads
        setSelectedNewsForLeads(newsItem);
        setShowLeadsDialog(true);
        
        if (data.count > 0) {
          toast.success(`Found ${data.count} potential leads`);
        } else {
          toast.info('No potential leads found in this article');
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to extract leads');
      }
    } catch (err) {
      toast.error('Failed to extract leads');
    } finally {
      setExtractingLeads(prev => ({ ...prev, [newsItem._id]: false }));
    }
  };

  // Delete news article (Admin only)
  const deleteNews = async (newsId) => {
    setDeletingNews(prev => ({ ...prev, [newsId]: true }));
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/news/${newsId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        // Remove news from local state
        setNews(prev => prev.filter(item => item._id !== newsId));
        setTotal(prev => prev - 1);
        toast.success(language === 'ar' ? 'تم حذف الخبر بنجاح' : 'News article deleted successfully');
      } else {
        const data = await response.json();
        if (response.status === 403) {
          toast.error(language === 'ar' ? 'الوصول مرفوض: مطلوب صلاحيات المسؤول' : 'Access denied: Admin privileges required');
        } else if (response.status === 404) {
          toast.error(language === 'ar' ? 'الخبر غير موجود' : 'News article not found');
        } else {
          toast.error(data.detail || (language === 'ar' ? 'فشل في حذف الخبر' : 'Failed to delete news'));
        }
      }
    } catch (err) {
      console.error('Delete exception:', err);
      toast.error(language === 'ar' ? 'فشل في حذف الخبر' : 'Failed to delete news');
    } finally {
      setDeletingNews(prev => ({ ...prev, [newsId]: false }));
    }
  };

  // Generate AI image for news article (Admin only)
  const generateImage = async (newsId) => {
    setGeneratingImage(prev => ({ ...prev, [newsId]: true }));
    toast.info(language === 'ar' ? 'جاري توليد الصورة... قد يستغرق هذا دقيقة' : 'Generating image... This may take a minute');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/news/${newsId}/generate-image`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      // Read the body exactly once to avoid "body stream already read"
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        // Update news item with new image
        setNews(prev => prev.map(item => 
          item._id === newsId 
            ? { ...item, image_url: data.image_url || item.image_url, image_source: 'ai_generated' }
            : item
        ));
        toast.success(language === 'ar' 
          ? `تم توليد الصورة بنجاح (${data.image_size_kb} KB)` 
          : `AI image generated successfully (${data.image_size_kb} KB)`);
        // Reload news to get the new image
        fetchNews();
      } else {
        toast.error(data.detail || (language === 'ar' ? 'فشل في توليد الصورة' : 'Failed to generate image'));
      }
    } catch (err) {
      console.error('Error generating image:', err);
      toast.error(language === 'ar' ? 'فشل في توليد الصورة' : 'Failed to generate image');
    } finally {
      setGeneratingImage(prev => ({ ...prev, [newsId]: false }));
    }
  };

  const saveToCRM = async (lead) => {
    setSavingLead(prev => ({ ...prev, [lead._id]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/opportunities/save-lead`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          lead_type: lead.lead_type,
          title: lead.title,
          description: lead.description,
          country: lead.country,
          sector: lead.sector,
          news_id: selectedNewsForLeads?._id,
          news_title: selectedNewsForLeads?.title_en || selectedNewsForLeads?.title
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Lead "${data.opportunity.name}" saved to CRM!`);
        // Update lead status in local state
        setNewsLeads(prev => ({
          ...prev,
          [selectedNewsForLeads._id]: prev[selectedNewsForLeads._id].map(l => 
            l._id === lead._id ? { ...l, saved_to_crm: true, ref_id: data.opportunity.ref_id } : l
          )
        }));
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save to CRM');
      }
    } catch (err) {
      toast.error('Failed to save to CRM');
    } finally {
      setSavingLead(prev => ({ ...prev, [lead._id]: false }));
    }
  };

  // Create custom lead from news
  const createCustomLead = async () => {
    if (!customLeadName.trim()) {
      toast.error(language === 'ar' ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    
    setCreatingCustomLead(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/leads/custom`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          lead_type: customLeadType,
          name: customLeadName.trim(),
          description: customLeadDescription.trim(),
          source_type: 'news',
          source_id: selectedNewsForLeads?._id,
          country: selectedNewsForLeads?.country,
          sector: selectedNewsForLeads?.sector
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create lead');
      }
      
      const data = await response.json();
      
      // Add the new lead to current news leads
      if (data.lead && selectedNewsForLeads) {
        setNewsLeads(prev => ({
          ...prev,
          [selectedNewsForLeads._id]: [...(prev[selectedNewsForLeads._id] || []), data.lead]
        }));
      }
      
      toast.success(language === 'ar' 
        ? 'تم إنشاء الفرصة بنجاح' 
        : 'Custom lead created successfully'
      );
      
      // Reset form and close dialog
      setCustomLeadName('');
      setCustomLeadDescription('');
      setCustomLeadType('project');
      setShowCustomLeadDialog(false);
    } catch (error) {
      console.error('Error creating custom lead:', error);
      toast.error(language === 'ar' 
        ? 'فشل في إنشاء الفرصة' 
        : error.message || 'Failed to create custom lead'
      );
    } finally {
      setCreatingCustomLead(false);
    }
  };

  const formatDate = (dateStr, tz) => {
    // WO-03 item 5 — render in the article's own country timezone (from the
    // API), not the reader's browser. One unambiguous long form ("1 November
    // 2026") in en and ar; never a numeric date.
    return formatDateInTZ(dateStr, tz, language);
  };

  const formatTimeAgo = (dateStr, tz) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return language === 'ar' ? 'الآن' : 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} ${language === 'ar' ? 'دقيقة' : 'min ago'}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${language === 'ar' ? 'ساعة' : 'hr ago'}`;
    return formatDate(dateStr, tz);
  };

  const translations = {
    en: {
      title: 'Market News',
      subtitle: 'Real-time intelligence from GCC markets',
      searchPlaceholder: 'Search news...',
      allCountries: 'All Countries',
      allSources: 'All Sources',
      allIndustries: 'All Industries',
      results: 'results',
      noNews: 'No news found',
      noNewsDesc: 'Try adjusting your filters or check back later',
      readMore: 'Read Article',
      aiSummary: 'AI Summary',
      source: 'Source',
      refresh: 'Refresh',
      page: 'Page',
      of: 'of',
      manageFeeds: 'Manage Feeds',
      feedSources: 'Feed Sources',
      hiddenFeeds: 'Hidden Feeds',
      showAll: 'Show All',
      hide: 'Hide',
      show: 'Show',
      leads: 'Leads',
      extractLeads: 'Extract Leads',
      extracting: 'Extracting...',
      potentialLeads: 'Potential Leads',
      saveToCRM: 'Save to CRM',
      saved: 'Saved',
      company: 'Company',
      project: 'Project',
      noLeads: 'No leads found in this article',
      viewLeads: 'View Leads',
      edit: 'Edit',
      delete: 'Delete'
    },
    ar: {
      title: 'أخبار السوق',
      subtitle: 'معلومات فورية من أسواق الخليج',
      searchPlaceholder: 'البحث في الأخبار...',
      allCountries: 'جميع الدول',
      allSources: 'جميع المصادر',
      allIndustries: 'جميع الصناعات',
      results: 'نتيجة',
      noNews: 'لا توجد أخبار',
      noNewsDesc: 'حاول تعديل عوامل التصفية أو تحقق لاحقًا',
      readMore: 'قراءة المقال',
      aiSummary: 'ملخص الذكاء الاصطناعي',
      source: 'المصدر',
      refresh: 'تحديث',
      page: 'صفحة',
      of: 'من',
      manageFeeds: 'إدارة المصادر',
      feedSources: 'مصادر الأخبار',
      hiddenFeeds: 'المصادر المخفية',
      showAll: 'إظهار الكل',
      hide: 'إخفاء',
      show: 'إظهار',
      leads: 'العملاء المحتملين',
      extractLeads: 'استخراج العملاء',
      extracting: 'جاري الاستخراج...',
      potentialLeads: 'العملاء المحتملين',
      saveToCRM: 'حفظ في CRM',
      saved: 'محفوظ',
      company: 'شركة',
      project: 'مشروع',
      noLeads: 'لم يتم العثور على عملاء محتملين في هذا المقال',
      viewLeads: 'عرض العملاء المحتملين',
      edit: 'تعديل',
      delete: 'حذف'
    }
  };

  const txt = translations[language];

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="news-page">
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg">
              <Newspaper className="w-7 h-7 text-white" />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground mt-2">{txt.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-wo40="header-actions">
          {/* Admin/Enterprise Create News Button */}
          {(isAdmin || userPlan === 'premium' || userPlan === 'enterprise') && (
            <Button 
              onClick={() => {
                setEditingNews(null);
                setShowAdminNewsDialog(true);
              }}
              className="flex items-center gap-2"
              data-testid="create-news-btn"
            >
              <Plus className="w-4 h-4" />
              {language === 'ar' ? 'إنشاء خبر' : 'Create News'}
            </Button>
          )}
          
          {/* Manage Feeds Button (admin-only) */}
          {isAdmin && (
          <Sheet open={showManageFeeds} onOpenChange={setShowManageFeeds}>
            <SheetTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2" data-testid="manage-feeds-btn">
                <Settings className="w-4 h-4" />
                {txt.manageFeeds}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[400px] sm:w-[540px]">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Rss className="w-5 h-5" />
                  {txt.manageFeeds}
                </SheetTitle>
                <SheetDescription>
                  {language === 'ar' ? 'تحكم في مصادر الأخبار التي تراها' : 'Control which news sources appear in your feed'}
                </SheetDescription>
              </SheetHeader>
              
              <Tabs defaultValue="sources" className="mt-6">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="sources">{txt.feedSources}</TabsTrigger>
                  <TabsTrigger value="hidden">{txt.hiddenFeeds}</TabsTrigger>
                </TabsList>
                
                <TabsContent value="sources" className="mt-4">
                  <ScrollArea className="h-[450px] pr-4">
                    <div className="space-y-4">
                      {/* Group sources by country */}
                      {(() => {
                        const countries = [
                          { code: 'SA', name: 'Saudi Arabia', nameAr: 'السعودية', flag: '🇸🇦' },
                          { code: 'AE', name: 'UAE', nameAr: 'الإمارات', flag: '🇦🇪' },
                          { code: 'OM', name: 'Oman', nameAr: 'عمان', flag: '🇴🇲' },
                          { code: 'KW', name: 'Kuwait', nameAr: 'الكويت', flag: '🇰🇼' },
                          { code: 'QA', name: 'Qatar', nameAr: 'قطر', flag: '🇶🇦' },
                          { code: 'BH', name: 'Bahrain', nameAr: 'البحرين', flag: '🇧🇭' }
                        ];
                        
                        return countries.map(country => {
                          const countrySources = feedSources.filter(fs => fs.country === country.code);
                          if (countrySources.length === 0) return null;
                          
                          const visibleCount = countrySources.filter(fs => !userPreferences.hidden_sources.includes(fs._id)).length;
                          const totalCount = countrySources.length;
                          
                          return (
                            <div key={country.code} className="border rounded-lg overflow-hidden">
                              {/* Country Header */}
                              <div className="bg-muted/50 px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl">{country.flag}</span>
                                  <div>
                                    <p className="font-semibold">{language === 'ar' ? country.nameAr : country.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {visibleCount}/{totalCount} {language === 'ar' ? 'مصادر نشطة' : 'sources active'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs"
                                    onClick={() => {
                                      countrySources.forEach(fs => {
                                        if (userPreferences.hidden_sources.includes(fs._id)) {
                                          toggleHideFeed(fs._id, fs.name);
                                        }
                                      });
                                    }}
                                  >
                                    {language === 'ar' ? 'إظهار الكل' : 'Show All'}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs"
                                    onClick={() => {
                                      countrySources.forEach(fs => {
                                        if (!userPreferences.hidden_sources.includes(fs._id)) {
                                          toggleHideFeed(fs._id, fs.name);
                                        }
                                      });
                                    }}
                                  >
                                    {language === 'ar' ? 'إخفاء الكل' : 'Hide All'}
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Sources List */}
                              <div className="divide-y">
                                {countrySources.map((feedSource) => {
                                  const isHidden = userPreferences.hidden_sources.includes(feedSource._id);
                                  return (
                                    <div 
                                      key={feedSource._id}
                                      className={`flex items-center justify-between px-4 py-3 ${isHidden ? 'bg-muted/30 opacity-60' : ''}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isHidden ? 'bg-muted' : 'bg-primary/10'}`}>
                                          <Rss className={`w-4 h-4 ${isHidden ? 'text-muted-foreground' : 'text-primary'}`} />
                                        </div>
                                        <div>
                                          <p className={`text-sm font-medium ${isHidden ? 'text-muted-foreground' : ''}`}>{feedSource.name}</p>
                                          <p className="text-xs text-muted-foreground">
                                            {feedSource.language === 'ar' ? '🔤 Arabic' : '🔤 English'}
                                          </p>
                                        </div>
                                      </div>
                                      <Switch
                                        checked={!isHidden}
                                        onCheckedChange={() => toggleHideFeed(feedSource._id, feedSource.name)}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </ScrollArea>
                </TabsContent>
                
                <TabsContent value="hidden" className="mt-4">
                  <ScrollArea className="h-[450px] pr-4">
                    <div className="space-y-4">
                      {(() => {
                        const hiddenSources = feedSources.filter(fs => userPreferences.hidden_sources.includes(fs._id));
                        
                        if (hiddenSources.length === 0) {
                          return (
                            <div className="text-center py-12 text-muted-foreground">
                              <Eye className="w-16 h-16 mx-auto mb-4 opacity-30" />
                              <p className="text-lg font-medium">{language === 'ar' ? 'لا توجد مصادر مخفية' : 'No hidden sources'}</p>
                              <p className="text-sm mt-2">{language === 'ar' ? 'جميع المصادر مفعلة حاليًا' : 'All sources are currently active'}</p>
                            </div>
                          );
                        }
                        
                        const countries = [
                          { code: 'SA', name: 'Saudi Arabia', nameAr: 'السعودية', flag: '🇸🇦' },
                          { code: 'AE', name: 'UAE', nameAr: 'الإمارات', flag: '🇦🇪' },
                          { code: 'OM', name: 'Oman', nameAr: 'عمان', flag: '🇴🇲' },
                          { code: 'KW', name: 'Kuwait', nameAr: 'الكويت', flag: '🇰🇼' },
                          { code: 'QA', name: 'Qatar', nameAr: 'قطر', flag: '🇶🇦' },
                          { code: 'BH', name: 'Bahrain', nameAr: 'البحرين', flag: '🇧🇭' }
                        ];
                        
                        return countries.map(country => {
                          const countryHiddenSources = hiddenSources.filter(fs => fs.country === country.code);
                          if (countryHiddenSources.length === 0) return null;
                          
                          return (
                            <div key={country.code} className="border rounded-lg overflow-hidden">
                              {/* Country Header */}
                              <div className="bg-muted/50 px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl">{country.flag}</span>
                                  <div>
                                    <p className="font-semibold">{language === 'ar' ? country.nameAr : country.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {countryHiddenSources.length} {language === 'ar' ? 'مصادر مخفية' : 'hidden sources'}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs"
                                  onClick={() => {
                                    countryHiddenSources.forEach(fs => {
                                      toggleHideFeed(fs._id, fs.name);
                                    });
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  {language === 'ar' ? 'إظهار الكل' : 'Show All'}
                                </Button>
                              </div>
                              
                              {/* Hidden Sources List */}
                              <div className="divide-y">
                                {countryHiddenSources.map((feedSource) => (
                                  <div 
                                    key={feedSource._id}
                                    className="flex items-center justify-between px-4 py-3 bg-muted/20"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center">
                                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-muted-foreground">{feedSource.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {feedSource.language === 'ar' ? '🔤 Arabic' : '🔤 English'}
                                        </p>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleHideFeed(feedSource._id, feedSource.name)}
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      {txt.show}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </SheetContent>
          </Sheet>
          )}
          
          <Button 
            variant="outline" 
            onClick={() => refreshNewsWithIngestion()}
            disabled={isRefreshing}
            className={`flex items-center gap-2 ${isAdmin ? '' : 'hidden'}`}
            title={language === 'ar'
              ? 'تحديث الأخبار: يجلب المقالات الأحدث من آخر تحديث من كل مصدر نشط.'
              : 'Refresh: fetches articles newer than the last refresh from every active source.'}
            data-testid="refresh-news-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? (language === 'ar' ? 'جارٍ التحديث…' : 'Refreshing…') : txt.refresh}
          </Button>

          {isAdmin && isRefreshing && refreshJob?.id && (
            <Button
              variant="outline"
              onClick={stopRefresh}
              className="flex items-center gap-2 text-red-600"
              title={language === 'ar' ? 'إيقاف التحديث الجاري. تبقى المقالات المحفوظة.' : 'Stop the running refresh. Articles already saved are kept.'}
              data-testid="stop-refresh-news-btn"
            >
              <Square className="w-4 h-4" />
              {language === 'ar' ? 'إيقاف' : 'Stop'}
            </Button>
          )}

          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => refreshNewsWithIngestion({ full: true })}
              disabled={isRefreshing}
              className="flex items-center gap-2"
              title={language === 'ar' ? 'إعادة فحص كاملة: تعيد قراءة كل ما في كل مصدر، لا الأحدث فقط. تُكمل ما فات في تحديث سابق. قد تستغرق عدة دقائق.' : 'Full Re-scan: reads everything each source offers, not only what is newer. Catches up what an earlier refresh missed. Can take several minutes.'}
              data-testid="full-rescan-news-btn"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {language === 'ar' ? 'إعادة فحص كاملة' : 'Full Re-scan'}
            </Button>
          )}

          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowBackfillConfirm(true)}
              disabled={isRefreshing}
              className="flex items-center gap-2"
              title={language === 'ar' ? 'سحب تاريخي عميق: يجلب أرشيف أخبار جوجل شهرياً منذ يناير 2026 (بطيء، 20-40 دقيقة)' : 'Deep Backfill: pulls the monthly Google News archive back to Jan 2026. Slow (20–40 min); run occasionally.'}
              data-testid="deep-backfill-news-btn"
            >
              <History className="w-4 h-4" />
              {language === 'ar' ? 'سحب تاريخي عميق' : 'Deep Backfill'}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={openDedupDialog}
              disabled={isRefreshing}
              className="flex items-center gap-2"
              data-testid="cleanup-duplicates-btn"
            >
              <Trash2 className="w-4 h-4" />
              {language === 'ar' ? 'إزالة التكرارات' : 'Cleanup Duplicates'}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={openReportDialog}
              className="flex items-center gap-2"
              data-testid="last-refresh-report-btn"
            >
              <FileText className="w-4 h-4" />
              {language === 'ar' ? 'تقرير آخر تحديث' : 'Last Refresh Report'}
            </Button>
          )}

          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => openTakedown(null)}
              className="flex items-center gap-2"
              data-testid="takedowns-btn"
            >
              <Shield className="w-4 h-4" />
              {language === 'ar' ? 'الاعتراضات' : 'Takedowns'}
            </Button>
          )}
        </div>
      </div>

      {/* WO-27 — Takedowns: record an objection, and show what is currently
          suppressed so it can be lifted. Nothing here deletes an article. */}
      <Dialog open={showTakedownDialog} onOpenChange={setShowTakedownDialog}>
        <DialogContent className="max-w-2xl" data-testid="takedown-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'اعتراضات الناشرين' : 'Publisher objections'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar'
                ? 'يُخفى المحتوى عن جميع المشتركين فورًا. لا يُحذف أي مقال، ويبقى السجل دليلًا على التنفيذ.'
                : 'Suppressed content disappears for every subscriber at once. Nothing is deleted — the record stays as evidence that we acted.'}
            </DialogDescription>
          </DialogHeader>

          {takedownItem && (
            <div className="space-y-4" data-testid="takedown-form">
              <div className="rounded-md border p-3 text-sm">
                <div className="font-medium">{takedownItem.display_title || takedownItem.title}</div>
                <div className="text-muted-foreground mt-1">
                  {takedownItem.publisher_name || (language === 'ar' ? 'تجميع صحفي' : 'Press aggregation')}
                </div>
              </div>

              <RadioGroup value={takedownScope} onValueChange={setTakedownScope}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="article" id="td-article" data-testid="takedown-scope-article" />
                  <Label htmlFor="td-article">
                    {language === 'ar' ? 'هذا المقال فقط' : 'This article only'}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="publisher"
                    id="td-publisher"
                    disabled={!takedownItem.publisher_name}
                    data-testid="takedown-scope-publisher"
                  />
                  <Label htmlFor="td-publisher">
                    {language === 'ar'
                      ? `كل ما ينشره ${takedownItem.publisher_name || ''} الآن ولاحقًا`
                      : `Everything from ${takedownItem.publisher_name || ''}, now and in future`}
                  </Label>
                </div>
              </RadioGroup>

              <div>
                <Label htmlFor="td-reason">
                  {language === 'ar' ? 'السبب (يُحفظ في السجل)' : 'Reason (kept in the record)'}
                </Label>
                <Textarea
                  id="td-reason"
                  value={takedownReason}
                  onChange={(e) => setTakedownReason(e.target.value)}
                  placeholder={language === 'ar' ? 'اعتراض ورد من الناشر بتاريخ…' : 'Objection received from the publisher on…'}
                  data-testid="takedown-reason"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setTakedownItem(null)}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button onClick={submitTakedown} disabled={takedownSaving} data-testid="takedown-submit">
                  {takedownSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {language === 'ar' ? 'تنفيذ الاعتراض' : 'Record the objection'}
                </Button>
              </DialogFooter>
            </div>
          )}

          {!takedownItem && (
            <div className="space-y-3" data-testid="takedown-list">
              <div className="text-sm font-medium">
                {language === 'ar' ? 'المعمول به الآن' : 'In force now'}
              </div>
              {!suppressions && (
                <div className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'جارٍ التحميل…' : 'Loading…'}
                </div>
              )}
              {suppressions && (suppressions.rules || []).length === 0
                && (suppressions.articles || []).length === 0 && (
                <div className="text-sm text-muted-foreground" data-testid="takedown-none">
                  {language === 'ar'
                    ? 'لا توجد اعتراضات. افتح قائمة أي مقال واختر «اعتراض ناشر».'
                    : 'No objections recorded. Open any article\u2019s menu and choose "Publisher objection".'}
                </div>
              )}
              <ScrollArea className="max-h-72">
                {suppressions && (suppressions.rules || []).map((rule, i) => (
                  <div
                    key={`${rule.scope}-${rule.value}-${i}`}
                    className="flex items-start justify-between gap-3 border-b py-2 text-sm"
                  >
                    <div>
                      <div className="font-medium">{rule.value}</div>
                      <div className="text-muted-foreground">
                        {rule.scope}
                        {rule.reason ? ` \u2014 ${rule.reason}` : ''}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => liftSuppression(rule)}
                      data-testid={`lift-${rule.scope}-${i}`}
                    >
                      {language === 'ar' ? 'إعادة' : 'Restore'}
                    </Button>
                  </div>
                ))}
                {/* WO-28 — an objection about ONE article writes no standing
                    rule, so it never appeared here and could not be lifted. */}
                {suppressions && (suppressions.articles || []).map((a, i) => (
                  <div
                    key={`article-${a._id}`}
                    className="flex items-start justify-between gap-3 border-b py-2 text-sm"
                    data-testid={`suppressed-article-${i}`}
                  >
                    <div>
                      <div className="font-medium">{a.title}</div>
                      <div className="text-muted-foreground">
                        {language === 'ar' ? 'مقال واحد' : 'single article'}
                        {a.publisher_name ? ` \u00b7 ${a.publisher_name}` : ''}
                        {a.reason ? ` \u2014 ${a.reason}` : ''}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => liftSuppression({ scope: 'article', value: a._id })}
                      data-testid={`lift-article-${i}`}
                    >
                      {language === 'ar' ? 'إعادة' : 'Restore'}
                    </Button>
                  </div>
                ))}
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* WO-28 — Upload a picture of our own onto a card. */}
      <Dialog open={!!uploadItem} onOpenChange={(o) => { if (!o) setUploadItem(null); }}>
        <DialogContent className="max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'رفع صورة' : 'Upload an image'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar'
                ? 'صور من إنتاجنا أو مرخّصة لنا فقط. لا تُرفع صورة مأخوذة من المقال — التطبيق يربط بصورة الناشر ولا يحتفظ بنسخة.'
                : 'Only pictures we made or own. Do not upload a picture taken from the article \u2014 the app links to the publisher\u2019s own picture rather than keeping a copy.'}
            </DialogDescription>
          </DialogHeader>

          {uploadItem && (
            <div className="space-y-4">
              <div className="rounded-md border p-3 text-sm">
                <div className="font-medium">{uploadItem.display_title || uploadItem.title}</div>
              </div>

              <div>
                <Label htmlFor="up-file">
                  {language === 'ar' ? 'الملف (JPEG أو PNG أو WebP، حتى 8 ميغابايت)' : 'File (JPEG, PNG or WebP, up to 8MB)'}
                </Label>
                <Input
                  id="up-file"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => setUploadFile(e.target.files && e.target.files[0])}
                  data-testid="upload-file"
                />
              </div>

              <RadioGroup value={uploadProvenance} onValueChange={setUploadProvenance}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ai_generated" id="up-ai" data-testid="upload-prov-ai" />
                  <Label htmlFor="up-ai">
                    {language === 'ar' ? 'أنشأتُها بالذكاء الاصطناعي' : 'I generated it myself'}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="licensed_or_own" id="up-own" data-testid="upload-prov-own" />
                  <Label htmlFor="up-own">
                    {language === 'ar' ? 'صورتي أو مرخّصة لي' : 'Mine, or licensed to me'}
                  </Label>
                </div>
              </RadioGroup>

              <div>
                <Label htmlFor="up-note">
                  {language === 'ar' ? 'من أين أتت (يُحفظ في السجل)' : 'Where it came from (kept on the article)'}
                </Label>
                <Input
                  id="up-note"
                  value={uploadNote}
                  onChange={(e) => setUploadNote(e.target.value)}
                  placeholder={language === 'ar' ? 'مثال: أُنشئت في ChatGPT، 18/09/2026' : 'e.g. generated in ChatGPT, 18/09/2026'}
                  data-testid="upload-note"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setUploadItem(null)}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button onClick={submitUpload} disabled={!uploadFile || uploadBusy} data-testid="upload-submit">
                  {uploadBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {language === 'ar' ? 'رفع' : 'Upload'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deep Backfill confirmation */}
      <AlertDialog open={showBackfillConfirm} onOpenChange={setShowBackfillConfirm}>
        <AlertDialogContent data-testid="deep-backfill-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'ar' ? 'بدء السحب التاريخي العميق؟' : 'Start Deep Backfill?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'ar'
                ? 'سيقوم هذا بجلب أرشيف أخبار جوجل شهرياً منذ يناير 2026 لكل عمليات بحث أخبار جوجل. العملية بطيئة (20-40 دقيقة تقريباً) وتعمل في الخلفية — يمكنك مغادرة الصفحة. سيتم تلخيص المقالات ذات الصلة بالقطاعات فقط. يكفي تشغيلها من حين لآخر.'
                : 'This pulls the monthly Google News archive back to January 2026 for every Google News search. It is slow (~20–40 min) and runs in the background — you can leave the page. Only industry-relevant articles are AI-summarized. Run it occasionally, not routinely.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deep-backfill-cancel">
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="deep-backfill-confirm"
              onClick={() => refreshNewsWithIngestion({ backfill: true })}
            >
              {language === 'ar' ? 'ابدأ السحب العميق' : 'Start Deep Backfill'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {refreshProgress !== null && (
        <Card className="border-0 bg-primary/5 backdrop-blur-sm" data-testid="news-refresh-progress">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2 min-w-0">
                  <Sparkles className="w-4 h-4 text-primary animate-pulse shrink-0" />
                  <span className="truncate" data-testid="news-refresh-status">{refreshStatus}</span>
                </span>
                <span className="font-semibold text-primary tabular-nums" data-testid="news-refresh-pct">
                  {Math.round(refreshProgress)}%
                </span>
              </div>

              {/* Smooth animated bar with a moving shimmer overlay */}
              <div className="relative">
                <Progress value={refreshProgress} className="h-2.5 transition-all duration-700 ease-out" />
                {refreshProgress < 100 && (
                  <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-full">
                    <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1.6s_infinite]" />
                  </div>
                )}
              </div>

              {/* Live running tally (b) */}
              {refreshDetail && (
                <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="news-refresh-tally">
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2.5 py-1 font-medium">
                    <TrendingUp className="w-3 h-3" /> {refreshDetail.totals?.new || 0} {language === 'ar' ? 'جديد' : 'new'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-400 px-2.5 py-1 font-medium">
                    <Sparkles className="w-3 h-3" /> {refreshDetail.totals?.enhanced || 0} {language === 'ar' ? 'معزز' : 'AI'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-1 font-medium">
                    {refreshDetail.totals?.skipped || 0} {language === 'ar' ? 'مستبعد' : 'skipped'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 font-medium ml-auto">
                    {refreshDetail.sourcesDone}/{refreshDetail.totalSources} {language === 'ar' ? 'مصادر' : 'sources'}
                  </span>
                </div>
              )}

              {/* Currently-active sources with article-level mini progress (a) */}
              {refreshDetail?.activeSources?.length > 0 && (
                <div className="space-y-1.5" data-testid="news-refresh-active">
                  {refreshDetail.activeSources.slice(0, 4).map((s) => {
                    const at = s.articles_total || 0;
                    const ad = s.articles_done || 0;
                    const sp = at > 0 ? Math.round((ad / at) * 100) : 0;
                    return (
                      <div key={s.id} className="flex items-center gap-2 text-xs">
                        <RefreshCw className="w-3 h-3 text-primary animate-spin shrink-0" />
                        <span className="truncate flex-1 text-foreground/80">{s.name}</span>
                        <span className="text-muted-foreground tabular-nums shrink-0">
                          {at > 0
                            ? `${ad}/${at} ${language === 'ar' ? 'مقالات' : 'articles'}`
                            : (language === 'ar' ? 'جارٍ الجلب…' : 'fetching…')}
                        </span>
                        {at > 0 && (
                          <div className="w-16 h-1 rounded-full bg-muted overflow-hidden shrink-0">
                            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${sp}%` }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recent completed-source log (b) */}
              {refreshDetail?.perSource?.length > 0 && (
                <div className="max-h-28 overflow-y-auto space-y-1 pr-1 border-t border-border/50 pt-2" data-testid="news-refresh-log">
                  {[...refreshDetail.perSource].slice(-8).reverse().map((s, i) => (
                    <div key={`${s.id}-${i}`} className="flex items-center gap-2 text-xs" title={[s.note, ...(s.errors || []), describeSkipReasons(s.skip_reasons)].filter(Boolean).join(' — ')}>
                      {s.status === 'success'
                        ? <span className="text-emerald-500 shrink-0">✓</span>
                        : (s.status === 'empty' || s.status === 'partial')
                          ? <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                          : <span className="text-red-500 shrink-0">✗</span>}
                      <span className="truncate flex-1 text-muted-foreground">{s.name}</span>
                      <span className="text-muted-foreground tabular-nums shrink-0">
                        {s.status === 'success' || s.status === 'partial'
                          ? `+${s.new || 0}`
                          : s.status === 'empty'
                            ? (language === 'ar' ? 'لا شيء' : 'nothing')
                            : (language === 'ar' ? 'فشل' : 'failed')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {!refreshDetail && (
                <p className="text-xs text-muted-foreground">
                  {language === 'ar'
                    ? 'جارٍ جلب الأخبار وترجمتها وتصنيفها حسب الصناعة...'
                    : 'Fetching news, translating to English, and classifying by industry...'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-0 bg-card/50 backdrop-blur-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className={`m2b-mfilters flex flex-col md:flex-row gap-3 flex-wrap ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className="m2b-keep relative flex-1 min-w-[200px]">
              <Search className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={txt.searchPlaceholder}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'} h-11`}
                data-testid="news-search-input"
              />
            </div>
            {/* WO-40 — phones: filters fold away behind this button (CSS in index.css, .m2b-mfilters) */}
            <label className="m2b-keep m2b-ftoggle md:hidden flex items-center justify-between h-11 px-4 border rounded-lg bg-background text-sm font-medium cursor-pointer select-none" data-testid="news-filters-toggle">
              <input type="checkbox" className="sr-only" aria-label={language === 'ar' ? 'إظهار عوامل التصفية' : 'Show filters'} />
              <span>{language === 'ar' ? 'عوامل التصفية' : 'Filters'}</span>
              <span className="m2b-ftoggle-arrow" aria-hidden="true">▾</span>
            </label>
            
            {/* Multi-select Country Filter */}
            <MultiSelectFilter
              label={txt.allCountries}
              icon={Globe}
              options={Object.keys(countryFlags).map(code => ({
                value: code,
                label: countryNames[language][code],
                icon: countryFlags[code],
                locked: isCountryLocked(code)
              }))}
              onLockedClick={handleLockedCountry}
              selected={selectedCountries}
              onChange={(values) => { setSelectedCountries(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="news-country-filter"
            />
            
            {/* WO-26 — subscribers filter by publisher. The source filter is
                our own internal map of where we read things, and it named
                Google as the source of other people's journalism. */}
            <MultiSelectFilter
              label={language === 'ar' ? 'كل الناشرين' : 'All Publishers'}
              icon={Newspaper}
              options={publishers.map(p => ({
                value: p.name,
                label: `${p.name} (${p.count})`,
              }))}
              selected={selectedPublishers}
              onChange={(values) => { setSelectedPublishers(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="news-publisher-filter"
            />

            {isAdmin && (
            <MultiSelectFilter
              label={txt.allSources}
              icon={Rss}
              options={feedSources.map(src => ({
                value: src.name,
                label: src.name,
                icon: countryFlags[src.country]
              }))}
              selected={selectedSources}
              onChange={(values) => { setSelectedSources(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="news-source-filter"
            />
            )}
            
            {/* Multi-select Industry Filter (grouped by category) */}
            <IndustryMultiSelectFilter
              label={txt.allIndustries}
              icon={Factory}
              selected={selectedIndustries}
              onChange={(values) => { setSelectedIndustries(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="news-industry-filter"
            />

            {/* Filter by linked Project */}
            <LinkedEntityFilter
              entityType="projects"
              selected={linkedProjectFilter}
              onChange={(val) => { setLinkedProjectFilter(val); setPage(1); }}
              language={language}
              token={token}
              testId="news-project-filter"
            />

            {/* Filter by linked Company */}
            <LinkedEntityFilter
              entityType="companies"
              selected={linkedCompanyFilter}
              onChange={(val) => { setLinkedCompanyFilter(val); setPage(1); }}
              language={language}
              token={token}
              testId="news-company-filter"
            />
            
            {/* Date Filter */}
            <DateFilter
              selectedPreset={datePreset}
              onPresetChange={(preset) => { setDatePreset(preset); setPage(1); }}
              dateRange={dateRange}
              onDateRangeChange={(range) => { setDateRange(range); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="news-date-filter"
            />
            
            {/* Favorites Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-background">
              <Heart className={`w-4 h-4 ${showFavoritesOnly ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
              <span className="text-sm whitespace-nowrap">
                {language === 'ar' ? 'المفضلة فقط' : 'Favorites only'}
              </span>
              <Switch
                checked={showFavoritesOnly}
                onCheckedChange={setShowFavoritesOnly}
                data-testid="news-favorites-toggle"
              />
            </div>

            <Button type="submit" className="m2b-keep bg-primary hover:bg-primary/90 h-11" data-testid="news-search-btn">
              <Search className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'بحث' : 'Search'}
            </Button>
          </form>
          
          {/* Active filters display */}
          {(selectedCountries.length > 0 || selectedSources.length > 0 || selectedIndustries.length > 0 || datePreset !== 'any' || linkedProjectFilter.length > 0 || linkedCompanyFilter.length > 0) && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50 flex-wrap">
              <span className="text-sm text-muted-foreground">
                {language === 'ar' ? 'الفلاتر النشطة:' : 'Active filters:'}
              </span>
              {selectedCountries.map(code => (
                <Badge key={code} variant="secondary" className="gap-1">
                  {countryFlags[code]} {countryNames[language][code]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCountries(prev => prev.filter(c => c !== code))} />
                </Badge>
              ))}
              {selectedSources.map(src => (
                <Badge key={src} variant="secondary" className="gap-1">
                  <Rss className="w-3 h-3" /> {src}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedSources(prev => prev.filter(s => s !== src))} />
                </Badge>
              ))}
              {selectedIndustries.map(ind => (
                <Badge key={ind} variant="secondary" className="gap-1">
                  <Factory className="w-3 h-3" /> {getIndustryLabel(ind, language)}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedIndustries(prev => prev.filter(i => i !== ind))} />
                </Badge>
              ))}
              {linkedProjectFilter.map(f => (
                <Badge key={f._id} variant="secondary" className="gap-1 border-primary/30 bg-primary/10 text-primary">
                  <Building2 className="w-3 h-3" /> {f.name}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setLinkedProjectFilter(prev => prev.filter(x => x._id !== f._id))} />
                </Badge>
              ))}
              {linkedCompanyFilter.map(f => (
                <Badge key={f._id} variant="secondary" className="gap-1 border-primary/30 bg-primary/10 text-primary">
                  <Building2 className="w-3 h-3" /> {f.name}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setLinkedCompanyFilter(prev => prev.filter(x => x._id !== f._id))} />
                </Badge>
              ))}
              {datePreset !== 'any' && (
                <Badge variant="secondary" className="gap-1">
                  <CalendarDays className="w-3 h-3" /> 
                  {datePreset === 'custom' && dateRange.from 
                    ? `${dateRange.from.toLocaleDateString()} - ${dateRange.to?.toLocaleDateString() || (language === 'ar' ? 'الآن' : 'Now')}`
                    : datePresets.find(p => p.id === datePreset)?.[language === 'ar' ? 'ar' : 'en']
                  }
                  <X className="w-3 h-3 cursor-pointer" onClick={() => { setDatePreset('any'); setDateRange({ from: null, to: null }); }} />
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setSelectedCountries([]); setSelectedSources([]); setSelectedIndustries([]); setDatePreset('any'); setDateRange({ from: null, to: null }); setLinkedProjectFilter([]); setLinkedCompanyFilter([]); }}
                className="text-xs h-7"
              >
                {language === 'ar' ? 'مسح الكل' : 'Clear all'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* WO-26 — the AI disclosure for the whole list, above the fold, in the
          subscriber's own language. Prescribed wording (BILAL, string A1). */}
      <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-xs text-muted-foreground" data-testid="news-ai-notice">
        {language === 'ar' ? 'مُلخّص أُنشئ آليًا بواسطة الذكاء الاصطناعي استنادًا إلى عنوان الناشر. لم يخضع لمراجعة تحريرية وقد يحتوي على أخطاء — يُرجى الرجوع إلى المقال الأصلي.' : 'Summary generated automatically by artificial intelligence from the publisher’s headline. It has not been reviewed by an editor and may contain errors — please read the original article.'}
      </div>

      {/* Results count */}
      {!loading && (
        <div className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
          {language === 'ar' 
            ? `عرض ${displayedNews.length} من ${total} مقالة${showFavoritesOnly ? ' (المفضلة)' : ''}` 
            : `Showing ${displayedNews.length} of ${total} articles${showFavoritesOnly ? ' (Favorites)' : ''}`
          }
        </div>
      )}

      {/* Linked filter banner */}
      {isFiltered && !loading && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm">
          <span className="font-medium text-primary">
            Showing {filterIds.length} linked article{filterIds.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => navigate('/dashboard/news')}
            className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            View all news <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* News Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border-0 bg-card/50 overflow-hidden">
              <Skeleton className="h-48 w-full" />
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-3/4" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-6 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-0 bg-destructive/10">
          <CardContent className="p-8 text-center">
            <p className="text-destructive">{error}</p>
            <Button onClick={() => fetchNews(false, true)} variant="outline" className="mt-4">
              {language === 'ar' ? 'إعادة المحاولة' : 'Try Again'}
            </Button>
          </CardContent>
        </Card>
      ) : displayedNews.length === 0 ? (
        <Card className="border-0 bg-card/50">
          <CardContent className="p-12 text-center">
            <Newspaper className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            {countryGate ? (
              <>
                <h3 className="text-xl font-semibold mb-2">{language === 'ar' ? 'سوق مقفل' : 'Locked market'}</h3>
                <p className="text-muted-foreground mb-4">{countryGate.message}</p>
                <Button data-testid="country-gate-upgrade-btn" onClick={() => { window.location.href = countryGate.upgrade_url || '/dashboard/settings?tab=plans'; }}>
                  {language === 'ar' ? 'ترقية الخطة' : 'Upgrade plan'}
                </Button>
              </>
            ) : (
            <OnboardingEmptyState
              kind="noResults"
              variant="inline"
              when={!!search || selectedCountries.length > 0 || selectedSources.length > 0 || selectedPublishers.length > 0 || selectedIndustries.length > 0 || datePreset !== 'any' || linkedProjectFilter.length > 0 || linkedCompanyFilter.length > 0}
              onClear={() => { setSelectedCountries([]); setSelectedSources([]); setSelectedPublishers([]); setSelectedIndustries([]); setDatePreset('any'); setDateRange({ from: null, to: null }); setLinkedProjectFilter([]); setLinkedCompanyFilter([]); setSearch(''); setSearchInput(''); }}
            >
            <>
            <h3 className="text-xl font-semibold mb-2">{txt.noNews}</h3>
            <p className="text-muted-foreground">{txt.noNewsDesc}</p>
            </>
            </OnboardingEmptyState>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {displayedNews.map((item) => (
            <div
              key={item._id}
              ref={item._id === deepLinkId ? deepLinkRef : null}
              className={item._id === deepLinkId ? 'ring-2 ring-primary ring-offset-2 rounded-xl' : ''}
            >
            <NewsCard
              item={item}
              isRTL={isRTL}
              txt={txt}
              language={language}
              formatDate={formatDate}
              formatTimeAgo={formatTimeAgo}
              extractLeads={extractLeads}
              extractingLeads={extractingLeads}
              newsLeads={newsLeads}
              isAdmin={isAdmin}
              onTakedown={openTakedown}
              onUploadImage={openUpload}
              onDelete={deleteNews}
              deletingNews={deletingNews}
              onGenerateImage={generateImage}
              generatingImage={generatingImage}
              onEdit={(news) => {
                setEditingNews(news);
                setShowAdminNewsDialog(true);
              }}
              token={token}
              canLink={canLinkNews(item)}
              onRefresh={fetchNews}
              isFavorite={isFavorite(item._id)}
              onFavoriteToggle={handleFavoriteToggle}
              onFilterByIndustry={(indId) => {
                setSelectedIndustries(prev => prev.includes(indId) ? prev : [...prev, indId]);
                setPage(1);
              }}
            />
            </div>
          ))}
        </div>
      )}

      {/* Admin/Enterprise News Dialog */}
      {(isAdmin || userPlan === 'premium' || userPlan === 'enterprise') && (
        <AdminNewsDialog
          open={showAdminNewsDialog}
          onOpenChange={(open) => {
            setShowAdminNewsDialog(open);
            if (!open) setEditingNews(null);
          }}
          news={editingNews}
          token={token}
          language={language}
          onSuccess={() => {
            fetchNews();
          }}
          onPlanGate={(pg) => planGate.show(pg)}
        />
      )}
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />

      {/* Leads Dialog */}
      <Dialog open={showLeadsDialog} onOpenChange={setShowLeadsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-accent" />
              {txt.potentialLeads}
            </DialogTitle>
            <DialogDescription>
              {selectedNewsForLeads?.title}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-4">
              {selectedNewsForLeads && newsLeads[selectedNewsForLeads._id]?.map((lead) => (
                <Card key={lead._id} className={`border ${lead.saved_to_crm ? 'bg-accent/5 border-accent/30' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={lead.lead_type === 'company' ? 'default' : 'secondary'}>
                            {lead.lead_type === 'company' ? (
                              <><Users className="w-3 h-3 mr-1" />{txt.company}</>
                            ) : (
                              <><Briefcase className="w-3 h-3 mr-1" />{txt.project}</>
                            )}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {Math.round(lead.confidence_score * 100)}% confidence
                          </Badge>
                        </div>
                        <h4 className="font-semibold mb-1">{lead.title}</h4>
                        <p className="text-sm text-muted-foreground line-clamp-2">{lead.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <span>{countryFlags[lead.country]}</span>
                          <span>{lead.sector}</span>
                        </div>
                      </div>
                      <div>
                        {lead.saved_to_crm ? (
                          <Badge className="bg-accent/10 text-accent border-accent/30">
                            <Check className="w-3 h-3 mr-1" />
                            {txt.saved}
                          </Badge>
                        ) : (
                          <Button 
                            size="sm"
                            onClick={() => saveToCRM(lead)}
                            disabled={savingLead[lead._id]}
                            data-testid={`save-lead-${lead._id}`}
                          >
                            {savingLead[lead._id] ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <ArrowRight className="w-3 h-3 mr-1" />
                            )}
                            {txt.saveToCRM}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {selectedNewsForLeads && (!newsLeads[selectedNewsForLeads._id] || newsLeads[selectedNewsForLeads._id].length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{txt.noLeads}</p>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCustomLeadDialog(true)}
              className="flex items-center gap-2"
              data-testid="add-custom-lead-btn"
            >
              <UserPlus className="w-4 h-4" />
              {language === 'ar' ? 'إضافة فرصة مخصصة' : 'Add Custom Lead'}
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setShowLeadsDialog(false)}>
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Lead Dialog */}
      <Dialog open={showCustomLeadDialog} onOpenChange={setShowCustomLeadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              {language === 'ar' ? 'إضافة فرصة مخصصة' : 'Add Custom Lead'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'أدخل تفاصيل الفرصة المخصصة من هذا الخبر'
                : 'Enter the details for a custom lead from this news article'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Lead Type Selection */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'نوع الفرصة' : 'Lead Type'}</Label>
              <RadioGroup 
                value={customLeadType} 
                onValueChange={setCustomLeadType}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="project" id="news-lead-type-project" />
                  <Label htmlFor="news-lead-type-project" className="flex items-center gap-2 cursor-pointer">
                    <Briefcase className="w-4 h-4 text-blue-500" />
                    {language === 'ar' ? 'مشروع' : 'Project'}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="company" id="news-lead-type-company" />
                  <Label htmlFor="news-lead-type-company" className="flex items-center gap-2 cursor-pointer">
                    <Users className="w-4 h-4 text-purple-500" />
                    {language === 'ar' ? 'شركة' : 'Company'}
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {/* Lead Name */}
            <div className="space-y-2">
              <Label htmlFor="news-custom-lead-name">
                {customLeadType === 'project' 
                  ? (language === 'ar' ? 'اسم المشروع' : 'Project Name')
                  : (language === 'ar' ? 'اسم الشركة' : 'Company Name')
                }
              </Label>
              <Input
                id="news-custom-lead-name"
                placeholder={customLeadType === 'project' 
                  ? (language === 'ar' ? 'أدخل اسم المشروع...' : 'Enter project name...')
                  : (language === 'ar' ? 'أدخل اسم الشركة...' : 'Enter company name...')
                }
                value={customLeadName}
                onChange={(e) => setCustomLeadName(e.target.value)}
                data-testid="news-custom-lead-name-input"
              />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="news-custom-lead-description">
                {language === 'ar' ? 'الوصف (اختياري)' : 'Description (optional)'}
              </Label>
              <Textarea
                id="news-custom-lead-description"
                placeholder={language === 'ar' ? 'أدخل وصفًا للفرصة...' : 'Enter a description for this lead...'}
                value={customLeadDescription}
                onChange={(e) => setCustomLeadDescription(e.target.value)}
                rows={3}
                data-testid="news-custom-lead-description-input"
              />
            </div>
            
            {/* Source Info */}
            {selectedNewsForLeads && (
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="text-muted-foreground mb-1">
                  {language === 'ar' ? 'المصدر:' : 'Source:'}
                </p>
                <p className="font-medium line-clamp-2">
                  {selectedNewsForLeads.title}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCustomLeadDialog(false);
                setCustomLeadName('');
                setCustomLeadDescription('');
              }}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={createCustomLead}
              disabled={!customLeadName.trim() || creatingCustomLead}
              data-testid="create-news-custom-lead-btn"
            >
              {creatingCustomLead ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'إنشاء الفرصة' : 'Create Lead'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Last Refresh Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {language === 'ar' ? 'تقرير آخر تحديث للأخبار' : 'Last News Refresh Report'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar'
                ? 'عدد الأخبار التي تم جلبها فعليًا من كل مصدر في آخر عملية تحديث.'
                : 'Articles actually retrieved from each source during the last refresh.'}
            </DialogDescription>
          </DialogHeader>

          {loadingReport ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : !lastReport ? (
            <div className="text-center py-10 text-muted-foreground" data-testid="report-empty">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
              {reportError
                ? <p className="text-red-600">{reportError}</p>
                : <p>{language === 'ar' ? 'لا يوجد تقرير تحديث بعد. قم بتحديث الأخبار أولاً.' : 'No refresh report yet. Run a news refresh first.'}</p>}
            </div>
          ) : (
            <div className="space-y-4" data-testid="last-refresh-report">
              {/* Summary chips */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium ${
                  lastReport.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600'
                  : lastReport.status === 'failed' ? 'bg-red-500/10 text-red-600'
                  : 'bg-muted text-muted-foreground'}`}>
                  {lastReport.status}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-600 px-2.5 py-1 font-medium">
                  <TrendingUp className="w-3 h-3" /> {lastReport.totals?.new || 0} {language === 'ar' ? 'جديد' : 'new'}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 text-violet-600 px-2.5 py-1 font-medium">
                  <Sparkles className="w-3 h-3" /> {lastReport.totals?.enhanced || 0} {language === 'ar' ? 'معزز' : 'AI'}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-1 font-medium">
                  {lastReport.totals?.skipped || 0} {language === 'ar' ? 'مستبعد' : 'skipped'}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 font-medium ml-auto">
                  {lastReport.sources_done || 0}/{lastReport.total_sources || 0} {language === 'ar' ? 'مصادر' : 'sources'}
                </span>
              </div>
              {lastReport.finished_at && (
                <p className="text-xs text-muted-foreground">
                  {language === 'ar' ? 'اكتمل في: ' : 'Finished: '}
                  {new Date(lastReport.finished_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                </p>
              )}

              {/* Per-source table */}
              <ScrollArea className="h-[360px] border rounded-lg">
                <table className="w-full text-sm">
                  {describeSkipReasons(lastReport.totals?.skip_reasons) && (
                    <caption className="caption-top text-left text-xs text-muted-foreground px-3 py-2" data-testid="report-skip-reasons">
                      {language === 'ar' ? 'أسباب الاستبعاد: ' : 'Why articles were not added: '}
                      {describeSkipReasons(lastReport.totals?.skip_reasons)}
                    </caption>
                  )}
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-medium">{language === 'ar' ? 'المصدر' : 'Source'}</th>
                      <th className="px-2 py-2 font-medium text-right">{language === 'ar' ? 'في الموجز' : 'In feed'}</th>
                      <th className="px-2 py-2 font-medium text-right">{language === 'ar' ? 'جديد' : 'New'}</th>
                      <th className="px-2 py-2 font-medium text-right">{language === 'ar' ? 'مستبعد' : 'Skip'}</th>
                      <th className="px-3 py-2 font-medium text-center">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(lastReport.per_source || []).map((s, i) => (
                      <tr key={`${s.id}-${i}`} className="border-t border-border/50" data-testid={`report-row-${s.id}`}>
                        <td className="px-3 py-2">
                          <div className="truncate max-w-[260px]" title={s.name}>{s.name}</div>
                          {s.errors?.length > 0 && (
                            <div className="text-xs text-red-500 truncate max-w-[260px]" title={s.errors.join('; ')}>
                              {s.errors[0]}
                            </div>
                          )}
                          {s.note && (
                            <div className="text-xs text-amber-600 truncate max-w-[260px]" title={s.note}>
                              {s.note}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">{s.in_feed ?? s.fetched ?? 0}</td>
                        <td className="px-2 py-2 text-right tabular-nums font-semibold text-emerald-600">{s.new || 0}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-muted-foreground" title={describeSkipReasons(s.skip_reasons)}>
                          {s.skipped || 0}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {s.status === 'success'
                            ? <Check className="w-4 h-4 text-emerald-500 inline" aria-label="OK" />
                            : (s.status === 'empty' || s.status === 'partial')
                              ? <AlertTriangle
                                  className="w-4 h-4 text-amber-500 inline"
                                  aria-label={s.status === 'empty'
                                    ? (language === 'ar' ? 'لم يُرجع المصدر شيئًا' : 'The source returned nothing')
                                    : (language === 'ar' ? 'توقف عند الحد الزمني' : 'Stopped at the time limit')}
                                />
                              : <X className="w-4 h-4 text-red-500 inline" aria-label={language === 'ar' ? 'فشل' : 'Failed'} />}
                        </td>
                      </tr>
                    ))}
                    {(lastReport.per_source || []).length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                        {language === 'ar' ? 'لا توجد بيانات لكل مصدر.' : 'No per-source data recorded.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </ScrollArea>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={fetchLastReport} disabled={loadingReport} data-testid="report-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingReport ? 'animate-spin' : ''}`} />
              {language === 'ar' ? 'تحديث' : 'Reload'}
            </Button>
            <Button onClick={() => setShowReportDialog(false)}>{language === 'ar' ? 'إغلاق' : 'Close'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cleanup Duplicates Dialog */}
      <Dialog open={showDedupDialog} onOpenChange={(o) => { if (!dedupRemoving) setShowDedupDialog(o); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              {language === 'ar' ? 'إزالة الأخبار المكررة' : 'Cleanup Duplicate News'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar'
                ? 'يبحث عن البطاقات المتشابهة جدًا (نفس الخبر من مصادر متعددة) ويحتفظ بأفضل نسخة فقط.'
                : 'Finds very similar cards (the same story from multiple sources) and keeps only the best copy.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {dedupScanning ? (
              <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground" data-testid="dedup-scanning">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                {language === 'ar' ? 'جارٍ فحص التكرارات…' : 'Scanning for duplicates…'}
              </div>
            ) : dedupResult ? (
              <>
                <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3" data-testid="dedup-summary">
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-destructive tabular-nums" data-testid="dedup-count">
                      {dedupResult.duplicate_count}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {language === 'ar'
                        ? `خبر مكرر في ${dedupResult.cluster_count} مجموعة`
                        : `duplicate articles across ${dedupResult.cluster_count} groups`}
                    </p>
                  </div>
                  {dedupDone && (
                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                      <Check className="w-3 h-3 mr-1" />
                      {language === 'ar' ? `تمت إزالة ${dedupDone.removed}` : `${dedupDone.removed} removed`}
                    </Badge>
                  )}
                </div>

                {dedupRemoving && (
                  <div className="space-y-1">
                    <Progress value={dedupProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground text-right">{dedupProgress}%</p>
                  </div>
                )}

                {/* Sample preview of clusters */}
                {dedupResult.sample?.length > 0 && !dedupDone && (
                  <ScrollArea className="h-[260px] border rounded-lg p-2" data-testid="dedup-sample">
                    <div className="space-y-3">
                      {dedupResult.sample.map((c, i) => (
                        <div key={i} className="text-xs border-b border-border/50 pb-2 last:border-0">
                          <div className="flex items-start gap-2">
                            <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                            <span className="flex-1"><span className="font-medium">{c.keep.title}</span>
                              <span className="text-muted-foreground"> · {c.keep.source}</span></span>
                          </div>
                          {c.remove.map((r, j) => (
                            <div key={j} className="flex items-start gap-2 mt-1 opacity-70">
                              <X className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                              <span className="flex-1 line-through">{r.title}
                                <span className="no-underline text-muted-foreground"> · {r.source}</span></span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDedupDialog(false)} disabled={dedupRemoving}>
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
            <Button variant="outline" onClick={() => runDedup(true)} disabled={dedupScanning || dedupRemoving} data-testid="dedup-rescan-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${dedupScanning ? 'animate-spin' : ''}`} />
              {language === 'ar' ? 'إعادة الفحص' : 'Re-scan'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => runDedup(false)}
              disabled={dedupScanning || dedupRemoving || !dedupResult || dedupResult.duplicate_count === 0 || !!dedupDone}
              data-testid="dedup-remove-btn"
            >
              {dedupRemoving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {language === 'ar' ? 'إزالة التكرارات' : 'Remove Duplicates'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load More Button */}
      {hasMore && displayedNews.length > 0 && (
        <div className="flex justify-center pt-8">
          <Button
            variant="outline"
            size="lg"
            onClick={loadMore}
            disabled={loadingMore}
            className="min-w-[200px]"
            data-testid="load-more-news"
          >
            {loadingMore ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'تحميل المزيد من المقالات' : 'Load More Articles'}
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* No more articles message */}
      {!hasMore && displayedNews.length > 0 && (
        <div className="text-center pt-8 text-muted-foreground">
          <p>{language === 'ar' ? 'لا يوجد المزيد من المقالات' : 'No more articles to load'}</p>
        </div>
      )}
    </div>
  );
}
